/**
 * VENDOR FORM WIZARD
 *
 * Multi-step wizard for creating new vendors with spacious layout design.
 *
 * Features:
 * - Three-step wizard: Primary Information → Financial Information → Bank Accounts
 * - Generous white space and breathing room
 * - Gray background field grouping with proper padding
 * - Custom label styling (small, secondary color)
 * - Sticky bottom buttons (Exit + Previous/Next/Submit)
 * - Sticky side steps with fixed height
 * - Scrollable content area
 * - React Hook Form integration
 * - Contacts and bank accounts management
 * - API integration for vendor creation
 */

import React, { useState, useEffect, useCallback, useRef, useReducer, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDebounceFn, useDebounce } from 'ahooks';
import {
  Form,
  Input,
  Select,
  Button,
  Layout,
  theme,
  App,
  Badge,
  Typography,
  Row,
  Col,
  Alert,
  Table,
  Radio,
  Dropdown,
  Tag,
  Spin,
  Modal,
  Checkbox,
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { type Contact, type VendorLocation } from '@/models/vendor';
import { useCurrencies } from '@/hooks/useCurrencies';
import BankAccountFormModal from '@/components/vendors/BankAccountFormModal';
import LocationFormModal, { type LocationFormData } from '@/components/vendors/LocationFormModal';
import CategorySelectWithCreate from '@/components/shared/CategorySelectWithCreate';
import TableContainer from '@/components/common/TableContainer';
import { ContactAddressDisplay } from '@/components/common/ContactAddressDisplay';
import { useCascadingLocations } from '@/hooks/useLocations';
import { usePaymentTerms } from '@/hooks/usePaymentTerms';
import { useTaxTypes } from '@/hooks/useTaxTypes';
import { useTranslation } from 'react-i18next';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import {
  useCheckVendorExists,
  useVendorMutations,
  useVendorDetail,
  useVendorLocations,
  useVendorLocationDetail,
  useLocationMutations,
} from '@/hooks/useVendors';
import { useContacts } from '@/hooks/useContacts';
import { useBankAccounts, useBankAccountMutations } from '@/hooks/useBanks';
import { useVendorPermissions, getToggleStatusStyle } from '@/hooks/useVendorPermissions';
import { usePayment, usePaymentMutations } from '@/hooks/usePayment';
import { locationApi, vendorApi, paymentApi, bankApi } from '@/services/api';
import { useFormStyles } from '@/styles/formStyles';
import type { BankAccountFormInput } from '@/types/vendors/bankAccount';
import type { CategoryListItem } from '@/types/api/category';
import { getCategories } from '@/apis/endpoints/categories';
import { useNavigationGuard } from '@/contexts/NavigationGuardContext';
import { useUnsavedChangesModal } from '@/components/common/ConfirmModal';
import { DEFAULT_COUNTRY_CODE } from '@/utils/phoneUtils';
import { formatAddressForDisplay, sanitizeLocationIds } from '@/utils/locationUtils';
import { parseEntityRouteId } from '@/utils/routeIdUtils';

const { Sider, Content } = Layout;
const { Text } = Typography;

interface FormValues {
  // Step 1: Primary Information
  alias: string;
  alternativeName?: string;
  nickname?: string;
  vendorType?: 'company' | 'individual'; // Vendor type: company or individual
  categoryIds: number[]; // Changed from category: string to support multiple categories
  salesRepresentativeIds?: number[]; // Array of user IDs, max 2
  notes?: string;
  group?: string;
  country: string;
  site?: string;
  stateId: number | null;
  state: string;
  cityId: number | null;
  citySub: string;
  address: string;
  postCode: string;
  primaryPort?: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  mobile?: string;
  email: string;
  web?: string;
  fax?: string;
  contacts: Contact[];
  locations: VendorLocation[];

  // Step 2: Financial Information (matches API)
  deposit?: number; // Deposit percentage (0-100)
  cashDiscount?: number; // Cash discount percentage (0-100)
  cashDiscountRate?: number; // Custom discount rate (0-100), maps to customDiscountRate - shown when payment term hasDiscount
  cashDiscountDays?: number; // Discount period in days, maps to customDiscountDays - shown when payment term hasDiscount
  termsTypeId?: number; // Payment term ID (will be set from API)
  termsDays?: number; // Number of days
  primaryCurrency: string; // 3-char code
  abn: string; // Tax ID
  taxTypeId?: number; // Tax type ID (will be set from API)
  taxRate?: number; // Fixed rate based on taxTypeId

  // Step 3: Bank Accounts
  bankAccounts: BankAccountFormInput[];
}

// Type for bank account table records (includes row metadata)
type BankAccountTableRecord = BankAccountFormInput & {
  key: number;
  originalIndex: number;
};

const VendorFormWizardContent = () => {
  const navigate = useNavigate();
  const { vendorId } = useParams<{ vendorId: string }>();
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { modal, notification, message: _message } = App.useApp();
  const { t, i18n } = useVendorTranslation();
  const { t: tCommon } = useTranslation('common');
  const { setHasUnsavedChanges: setGlobalUnsavedChanges } = useNavigationGuard();
  const { confirmUnsavedChanges } = useUnsavedChangesModal();

  // Edit mode detection
  const isEditMode = !!vendorId;
  // Tolerant of the real BE's "TRN-0001" string list ids (D-VEND-NEW-1)
  const numericVendorId = parseEntityRouteId(vendorId);

  // Helper to check if an item is newly created (not yet saved to API)
  // Items from API have IDs like "contact-123", "location-456" (small numbers)
  // New items have IDs like "contact-1732625440905" (timestamp - large numbers > 1000000000)
  const isNewItem = (id: string): boolean => {
    // Safety check: if id is not a valid string, treat as new to avoid errors
    if (!id || typeof id !== 'string') return true;
    const match = id.match(/\d+$/);
    if (!match) return true;
    const numericPart = parseInt(match[0], 10);
    // Timestamps are > 1 billion, API IDs are typically < 1 million
    return numericPart > 1000000000;
  };

  const [currentStep, setCurrentStep] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [stepErrors, setStepErrors] = useState<{ [key: number]: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);
  // #91 — contacts are created/edited ONLY in the Contacts module; the wizard
  // renders a view-only contact list (edit mode) and links out for creation.

  // Location state
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<VendorLocation | null>(null);
  const [editingLocationFormData, setEditingLocationFormData] = useState<LocationFormData | null>(
    null
  );
  // For edit mode: trigger API fetch when editing existing location
  const [editingLocationId, setEditingLocationId] = useState<number | null>(null);

  // Include inactive filters (edit mode only)
  const [includeInactiveContacts, setIncludeInactiveContacts] = useState(false);
  const [includeInactiveLocations, setIncludeInactiveLocations] = useState(false);
  const [includeInactiveBankAccounts, setIncludeInactiveBankAccounts] = useState(false);

  const [_windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [categories, setCategories] = useState<CategoryListItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Mock sales representatives data (TODO: Replace with API call)
  const salesRepresentatives = [
    { userId: 1, name: 'John Smith' },
    { userId: 2, name: 'Sarah Johnson' },
    { userId: 3, name: 'Michael Chen' },
    { userId: 4, name: 'Emily Davis' },
    { userId: 5, name: 'David Wilson' },
  ];

  // Bank account state
  const [bankAccountModalOpen, setBankAccountModalOpen] = useState(false);
  const [editingBankAccountIndex, setEditingBankAccountIndex] = useState<number | null>(null);
  const [bankAccountReadOnly, setBankAccountReadOnly] = useState(false);

  // Vendor existence check state
  const [countryId, setCountryId] = useState<number | null>(null);
  const [localTaxId, setLocalTaxId] = useState<string | null>(null);
  const [_hasProcessedCheck, _setHasProcessedCheck] = useState(false);

  // Debounce the localTaxId to avoid excessive API calls while user is typing
  const debouncedLocalTaxId = useDebounce(localTaxId, { wait: 800 });

  // ============================================================================
  // VALIDATION STATE MACHINE using useReducer
  // ============================================================================

  // Session tracking: Map of quick-created vendors in this session
  // Key: "countryId-localTaxId", Value: vendorId
  const sessionCreatedVendorsRef = useRef<Map<string, number>>(new Map());

  // Track if user has manually deleted a location to prevent auto-create from re-adding
  const userDeletedLocationRef = useRef(false);

  // Reset the deletion flag when navigating to a new vendor (create or different edit)
  useEffect(() => {
    userDeletedLocationRef.current = false;
  }, [vendorId]);

  type ValidationMachineState = {
    status:
      | 'IDLE'
      | 'VALIDATING'
      | 'VENDOR_EXISTS'
      | 'COMPANY_FOUND'
      | 'MANUAL_ENTRY'
      | 'PRE_FILLED';
    localTaxId: string | null;
    companyName: string | null;
    message: string | null;
    showModal: boolean;
    showWarningBanner: boolean;
    vendorIdToReload: number | null; // For SESSION_VENDOR_RELOAD - auto-load vendor data
  };

  type ValidationMachineAction =
    | { type: 'START_VALIDATION'; localTaxId: string }
    | {
        type: 'VALIDATION_COMPLETE';
        result: 'vendor_exists' | 'company_found' | 'not_found';
        vendorName?: string;
        message?: string;
      }
    | { type: 'SESSION_VENDOR_RELOAD'; vendorId: number } // Vendor exists but was created in this session
    | { type: 'USER_TYPING' }
    | { type: 'MODAL_ACCEPTED' }
    | { type: 'MODAL_CANCELLED' }
    | { type: 'COUNTRY_CHANGED' }
    | { type: 'RESET' };

  const validationMachineReducer = (
    state: ValidationMachineState,
    action: ValidationMachineAction
  ): ValidationMachineState => {
    switch (action.type) {
      case 'USER_TYPING':
        // User is typing - reset to idle and close modal
        return {
          ...state,
          status: 'IDLE',
          showModal: false,
          message: null,
          vendorIdToReload: null,
        };

      case 'START_VALIDATION':
        // Debounce completed, start validation
        return {
          ...state,
          status: 'VALIDATING',
          localTaxId: action.localTaxId,
          showModal: false,
          vendorIdToReload: null,
        };

      case 'VALIDATION_COMPLETE':
        if (action.result === 'vendor_exists') {
          return {
            ...state,
            status: 'VENDOR_EXISTS',
            companyName: action.vendorName || null, // Store vendor name for i18n
            message: null, // Don't use API message, use translation in UI
            showModal: false,
            showWarningBanner: false,
          };
        } else if (action.result === 'company_found') {
          return {
            ...state,
            status: 'COMPANY_FOUND',
            message: action.message || 'Company found',
            showModal: true, // Show modal immediately
            showWarningBanner: false,
          };
        } else {
          return {
            ...state,
            status: 'MANUAL_ENTRY',
            message: null,
            showModal: false,
            showWarningBanner: false,
          };
        }

      case 'MODAL_ACCEPTED':
        // User clicked "Use This Company"
        return {
          ...state,
          status: 'PRE_FILLED',
          showModal: false,
          showWarningBanner: true,
          vendorIdToReload: null, // Data already loaded in handleLoadCompanyData
        };

      case 'SESSION_VENDOR_RELOAD':
        // Vendor exists but was created in this session - allow re-loading
        return {
          ...state,
          status: 'PRE_FILLED',
          showModal: false,
          showWarningBanner: true,
          message: null,
          vendorIdToReload: action.vendorId, // Trigger auto-load effect
        };

      case 'MODAL_CANCELLED':
        // User cancelled modal - go to manual entry
        return {
          ...state,
          status: 'MANUAL_ENTRY',
          showModal: false,
          message: null,
        };

      case 'COUNTRY_CHANGED':
      case 'RESET':
        // Reset everything
        return {
          status: 'IDLE',
          localTaxId: null,
          companyName: null,
          message: null,
          showModal: false,
          showWarningBanner: false,
          vendorIdToReload: null,
        };

      default:
        return state;
    }
  };

  const [validationMachine, dispatchValidation] = useReducer(validationMachineReducer, {
    status: 'IDLE',
    localTaxId: null,
    companyName: null,
    message: null,
    showModal: false,
    showWarningBanner: false,
    vendorIdToReload: null,
  });

  // Separate loading state for modal data fetching
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);

  // Track window height changes (currently using CSS calc(), but available for future enhancements)
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch categories from API (reusable function for refresh)
  // Only fetch active categories (isActive=1) since inactive categories shouldn't be assignable
  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const response = await getCategories({ isActive: 1, pageSize: -1 }); // All active categories (pageSize: -1 = all)
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      notification.error({
        message: t('vendor.notifications.systemError'),
        description: t('vendor.notifications.categoriesLoadFailed'),
        placement: 'topRight',
        duration: 5,
      });
    } finally {
      setLoadingCategories(false);
    }
  }, [t, notification]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    watch,
    setValue,
    trigger,
    getValues,
    reset,
  } = useForm<FormValues>({
    mode: 'onTouched', // Validate on first blur, then on every change after
    reValidateMode: 'onChange', // Re-validate on change after first error
    defaultValues: {
      alias: '',
      alternativeName: '',
      nickname: '',
      vendorType: 'company', // Default to company (individual: false)
      categoryIds: [],
      salesRepresentativeIds: [],
      notes: '',
      country: '',
      stateId: null,
      state: '',
      cityId: null,
      citySub: '',
      address: '',
      postCode: '',
      primaryPort: '',
      phoneCountryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber: '',
      phoneExtension: '',
      email: '',
      web: '',
      fax: '',
      contacts: [],
      locations: [],
      deposit: undefined,
      cashDiscount: undefined,
      cashDiscountRate: undefined,
      cashDiscountDays: undefined,
      termsTypeId: undefined, // Will be set from API after loading
      termsDays: undefined,
      primaryCurrency: 'USD',
      abn: '',
      taxTypeId: undefined, // Will be set from API after loading
      taxRate: undefined,
      bankAccounts: [],
    },
  });

  const selectedCountry = watch('country');
  const selectedStateId = watch('stateId');
  const selectedAbn = watch('abn');
  const termsTypeId = watch('termsTypeId');
  const taxTypeId = watch('taxTypeId');
  const contacts = watch('contacts') || [];
  const locations = watch('locations') || [];
  const bankAccounts = watch('bankAccounts') || [];

  // Fetch payment terms from API (typeId: 1 = Vendor)
  const { paymentTerms, isLoading: isLoadingPaymentTerms } = usePaymentTerms(1);

  // Fetch tax types based on selected country (countryId is derived from selectedCountry)
  const { taxTypes, isLoading: isLoadingTaxTypes } = useTaxTypes(countryId ?? undefined);

  // Fetch currencies from API
  const { currencyOptions, isLoading: isLoadingCurrencies } = useCurrencies();

  // Vendor mutations for create/update (handle cache invalidation)
  const { createVendor, updateVendor: updateVendorMutation } = useVendorMutations();

  // Load vendor data if in edit mode
  const { vendor: existingVendor, isLoading: isLoadingVendor } = useVendorDetail(numericVendorId);

  // Vendor permissions - controls edit/add actions based on vendor status
  // Pass isCreateMode to enable all actions when creating a new vendor
  const { getItemPermissions } = useVendorPermissions(existingVendor, {
    isCreateMode: !isEditMode,
  });

  // Load related data when in edit mode
  const { contacts: existingContacts, isLoading: isLoadingContacts } = useContacts(
    isEditMode ? numericVendorId : null
  );
  const { locations: existingLocations, isLoading: isLoadingLocations } = useVendorLocations(
    isEditMode ? numericVendorId : null
  );
  const { bankAccounts: existingBankAccounts, isLoading: isLoadingBankAccounts } = useBankAccounts(
    isEditMode ? numericVendorId : null
  );
  const { paymentData: existingPaymentData, isLoading: isLoadingPaymentData } = usePayment(
    isEditMode ? numericVendorId : null
  );

  // Mutation hooks for edit mode cumulative save
  // These hooks require a valid vendorId, so we use 0 as fallback (won't be called in create mode)
  const {
    createBankAccount,
    updateBankAccount,
    setDefault: setDefaultBankAccount,
  } = useBankAccountMutations(numericVendorId ?? 0);
  const { updatePaymentData } = usePaymentMutations(numericVendorId ?? 0);
  const locationMutations = useLocationMutations(numericVendorId ?? 0);

  // Fetch individual location detail for edit modal (only when editing existing items in edit mode)
  const { location: locationDetail, isLoading: locationDetailLoading } = useVendorLocationDetail(
    isEditMode ? numericVendorId : null,
    editingLocationId
  );

  // Transform location detail from API to form data
  const locationDetailFormData = useMemo((): LocationFormData | null => {
    if (!locationDetail) return null;
    return {
      locationName: locationDetail.locationName,
      isDefaultReceiving: locationDetail.isDefaultReceiving,
      country: locationDetail.country,
      countryId: locationDetail.countryId ?? null,
      stateId: locationDetail.stateId ?? null,
      state: locationDetail.state ?? '',
      cityId: locationDetail.cityId ?? null,
      city: locationDetail.city ?? '',
      postcode: locationDetail.postcode ?? '',
      address: locationDetail.address ?? '',
      addressLang: locationDetail.addressLang ?? null,
      notes: locationDetail.notes ?? '',
    };
  }, [locationDetail]);

  // Use existing hook for vendor existence check (with debounced tax ID) - only in create mode
  const {
    exists,
    companyExists,
    existingVendorId,
    existingVendorName,
    existingCompany: existingCompanyFromCheck,
    message: checkMessage,
    isLoading: isCheckingVendor,
  } = useCheckVendorExists(isEditMode ? null : countryId, isEditMode ? '' : debouncedLocalTaxId);

  // Track initialization to avoid re-running (use state instead of ref to trigger re-renders)
  const [hasLoadedEditData, setHasLoadedEditData] = useState(false); // For edit mode 3-stage loading
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false); // For payment terms/tax types

  // Mark form as initialized after default values are set
  useEffect(() => {
    // Wait for payment terms and tax types to load and be set
    if (paymentTerms.length > 0 && taxTypes.length > 0 && !hasInitializedDefaults) {
      // Mark as initialized immediately after defaults are available
      setHasInitializedDefaults(true);
      // Only set form as initialized in create mode (edit mode will set this after Stage 1)
      if (!isEditMode) {
        setIsFormInitialized(true);
      }
    }
  }, [paymentTerms.length, taxTypes.length, hasInitializedDefaults, isEditMode]);

  // Track form dirty state (only after form is initialized)
  useEffect(() => {
    if (isFormInitialized) {
      setHasUnsavedChanges(isDirty);
    }
  }, [isDirty, isFormInitialized]);

  // Sync local unsaved changes state with global context
  useEffect(() => {
    setGlobalUnsavedChanges(hasUnsavedChanges);
    // Cleanup on unmount
    return () => {
      setGlobalUnsavedChanges(false);
    };
  }, [hasUnsavedChanges, setGlobalUnsavedChanges]);

  // FSM Effect 1: User typing - dispatch USER_TYPING action
  useEffect(() => {
    if (isEditMode) return;

    // Update ABN for validation hook
    setLocalTaxId(selectedAbn || null);

    // If user is typing (value doesn't match debounced), dispatch USER_TYPING
    if (selectedAbn && selectedAbn !== debouncedLocalTaxId) {
      dispatchValidation({ type: 'USER_TYPING' });
    }
  }, [selectedAbn, debouncedLocalTaxId, isEditMode]);

  // FSM Effect 2: Debounce complete - start validation
  useEffect(() => {
    if (isEditMode) return;
    if (!debouncedLocalTaxId || !countryId) {
      dispatchValidation({ type: 'RESET' });
      return;
    }

    // Debounced value ready - start validation
    dispatchValidation({ type: 'START_VALIDATION', localTaxId: debouncedLocalTaxId });
  }, [debouncedLocalTaxId, countryId, isEditMode]);

  // FSM Effect 3: Validation results - dispatch VALIDATION_COMPLETE
  useEffect(() => {
    if (isEditMode) return;
    if (validationMachine.status !== 'VALIDATING') return;
    if (isCheckingVendor) return; // Still loading

    // Validation complete - dispatch result
    if (exists) {
      // Check if this vendor was created in this session
      const sessionKey = `${countryId}-${debouncedLocalTaxId}`;
      const sessionVendorId = sessionCreatedVendorsRef.current.get(sessionKey);

      if (sessionVendorId && existingVendorId === sessionVendorId) {
        // This vendor was created in this session - allow re-loading
        dispatchValidation({
          type: 'SESSION_VENDOR_RELOAD',
          vendorId: sessionVendorId,
        });
      } else {
        // Vendor exists but was NOT created in this session - show error
        dispatchValidation({
          type: 'VALIDATION_COMPLETE',
          result: 'vendor_exists',
          vendorName: existingVendorName,
        });
      }
    } else if (companyExists && existingCompanyFromCheck) {
      // Company exists but is not registered as a vendor - show modal to use existing data
      dispatchValidation({
        type: 'VALIDATION_COMPLETE',
        result: 'company_found',
        message: checkMessage || 'Company found',
      });
    } else {
      dispatchValidation({
        type: 'VALIDATION_COMPLETE',
        result: 'not_found',
      });
    }
  }, [
    validationMachine.status,
    isCheckingVendor,
    exists,
    existingVendorId,
    existingVendorName,
    companyExists,
    existingCompanyFromCheck,
    checkMessage,
    isEditMode,
    countryId,
    debouncedLocalTaxId,
  ]);

  // FSM Effect 4: Auto-load vendor data when SESSION_VENDOR_RELOAD is triggered
  useEffect(() => {
    const vendorId = validationMachine.vendorIdToReload;
    if (!vendorId) return;

    const loadSessionVendorData = async () => {
      try {
        const vendorDetail = await vendorApi.getById(vendorId);
        if (!vendorDetail) {
          throw new Error('Failed to fetch vendor details');
        }

        // Pre-fill form fields with existing vendor data
        // Basic Information
        setValue('alias', vendorDetail.name || '');
        setValue('alternativeName', vendorDetail.alternativeName || '');
        setValue('nickname', vendorDetail.nickname || '');
        if (vendorDetail.vendorType) {
          setValue('vendorType', vendorDetail.vendorType);
        }
        if (vendorDetail.categories && vendorDetail.categories.length > 0) {
          setValue(
            'categoryIds',
            vendorDetail.categories.map((cat) => cat.categoryId)
          );
        }
        setValue('primaryPort', vendorDetail.primaryPort || '');
        setValue('notes', vendorDetail.notes || '');

        // Registered Address — board2-#79: single-address entry; stateId/cityId
        // are backend-parse OUTPUTS and are not pre-filled into the form
        setValue('address', vendorDetail.address || '');
        setValue('postCode', vendorDetail.postcode || '');

        // Contact Details - Use phoneCountryCode directly from API
        setValue('phoneCountryCode', vendorDetail.phoneCountryCode || DEFAULT_COUNTRY_CODE);
        setValue('phoneNumber', vendorDetail.phoneNumber || '');
        setValue('phoneExtension', vendorDetail.phoneExtension || '');

        if (vendorDetail.mobile) {
          setValue('mobile', vendorDetail.mobile);
        }
        setValue('email', vendorDetail.email || '');
        setValue('fax', vendorDetail.fax || '');
        setValue('web', vendorDetail.web || '');

        // Clear step 0 validation errors since form is now populated
        setStepErrors((prev) => ({ ...prev, 0: false }));
      } catch (error) {
        console.error('Failed to load session vendor data:', error);
        notification.error({
          message: t('vendor.notifications.loadFailed'),
          description: t('vendor.notifications.loadFailedDescription'),
          placement: 'topRight',
          duration: 5,
        });
      }
    };

    loadSessionVendorData();
  }, [validationMachine.vendorIdToReload, setValue, setStepErrors, t, notification]);

  // Fetch cascading location data
  // states/cities are only used to resolve display names when the postcode
  // lookup pre-resolved the parse-output ids (board2-#79)
  const { countries, states, cities, isLoadingCountries } = useCascadingLocations(
    selectedCountry || null,
    selectedStateId
  );

  // Handle country change — clear the stale state/city PARSE OUTPUTS
  // (board2-#79: no state/city Selects; a country change invalidates any
  // previously parsed split, the backend re-parses on save)
  const handleCountryChange = useCallback(
    (value: string) => {
      setValue('country', value);
      setValue('stateId', null);
      setValue('state', '');
      setValue('cityId', null);
      setValue('citySub', '');

      // Update countryId for validation
      const country = countries.find((c) => c.countryCode === value);
      setCountryId(country?.countryId || null);

      // Reset validation machine when country changes
      dispatchValidation({ type: 'COUNTRY_CHANGED' });
      setStepErrors((prev) => ({ ...prev, 0: false }));
    },
    [setValue, countries]
  );

  // Extract countryId from country code
  useEffect(() => {
    if (selectedCountry) {
      if (countries.length > 0) {
        const country = countries.find((c) => c.countryCode === selectedCountry);
        if (country) {
          setCountryId(country.countryId);
        } else {
          setCountryId(null);
        }
      } else {
        // Countries not loaded yet - show warning if user is trying to check vendor
        if (selectedAbn && selectedAbn.trim().length > 0) {
          console.warn('Cannot check vendor existence: countries data not loaded');
        }
        setCountryId(null);
      }
    } else {
      setCountryId(null);
    }
  }, [selectedCountry, countries, selectedAbn]);

  // Update localTaxId when ABN changes
  useEffect(() => {
    setLocalTaxId(selectedAbn || null);
  }, [selectedAbn]);

  // Watch values for auto-create location logic
  const watchedStateId = watch('stateId');
  const watchedCityId = watch('cityId');
  const watchedAddress = watch('address');
  const watchedPostCode = watch('postCode');
  const watchedLocationsRaw = watch('locations');

  // Reactive auto-create location effect using touchedFields
  // This effect watches ALL required conditions and triggers auto-create when:
  // 1. All required field VALUES are filled
  // 2. All required fields have been TOUCHED (focused + blurred = user committed the field)
  //
  // board2-#79 single-address entry: the committed set is country / address /
  // postcode — state/city are backend-parse OUTPUTS and no longer gate the
  // auto-create (the postcode lookup may pre-resolve them; otherwise the row
  // is created unparsed and the backend/mock parse fills them on save).
  //
  // Key insight: touchedFields tracks which fields user has explicitly interacted with and left.
  // - For Select fields: touched immediately when user makes a selection
  // - For Input fields: touched when user blurs (finishes typing)
  // This handles ANY field order without needing debounce.
  useEffect(() => {
    // Skip if:
    // - Form not initialized (avoid triggering on mount)
    // - User has manually deleted a location (respect user's decision)
    // - Already has locations (auto-create is one-time only)
    if (
      !isFormInitialized ||
      userDeletedLocationRef.current ||
      (watchedLocationsRaw || []).length > 0
    ) {
      return;
    }

    // Check all field VALUES are filled (country + address + postcode)
    const hasAllRequiredValues = countryId && watchedAddress && watchedPostCode;

    if (!hasAllRequiredValues) {
      return;
    }

    // Check all required fields have been TOUCHED (user committed each field)
    // This ensures we don't trigger while user is still typing
    const allFieldsTouched =
      touchedFields.country && touchedFields.address && touchedFields.postCode;

    if (!allFieldsTouched) {
      return;
    }

    // All conditions met - resolve display names and create location immediately.
    // State/city names resolve only when the postcode lookup already filled the
    // ids; an unparsed row keeps nulls (display components render a dash).
    const countryName = countries.find((c) => c.countryId === countryId)?.countryName || '';
    const stateName = watchedStateId
      ? states.find((s) => s.stateId === watchedStateId)?.stateName || ''
      : '';
    const cityName = watchedCityId
      ? cities.find((c) => c.cityId === watchedCityId)?.cityName || ''
      : '';

    const firstLocation: VendorLocation = {
      id: `location-registered-address`,
      isDefaultReceiving: true,
      locationName: 'Registered Address',
      country: countryName,
      countryId: countryId,
      stateId: watchedStateId ?? null,
      state: stateName,
      cityId: watchedCityId ?? null,
      city: cityName,
      postcode: watchedPostCode,
      address: watchedAddress,
      addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en',
      notes: 'Auto-created from registered address',
    };

    setValue('locations', [firstLocation], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    notification.info({
      message: t('vendor.messages.locationCreated'),
      description: t('vendor.messages.locationAutoCreatedDescription'),
      icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
      placement: 'topRight',
      duration: 4,
    });
  }, [
    isFormInitialized,
    countryId,
    watchedStateId,
    watchedCityId,
    watchedAddress,
    watchedPostCode,
    watchedLocationsRaw,
    states,
    cities,
    countries,
    touchedFields.country,
    touchedFields.address,
    touchedFields.postCode,
    i18n.language,
    setValue,
    notification,
    token,
    t,
  ]);

  // Handler for loading company data after confirmation
  // Uses existing company data from check-exists API response
  const handleLoadCompanyData = () => {
    if (!existingCompanyFromCheck) {
      notification.error({
        message: t('vendor.notifications.quickCreateFailed'),
        description: t('vendor.notifications.quickCreateFailedDescription'),
        placement: 'topRight',
        duration: 5,
      });
      return;
    }

    setIsLoadingCompanyData(true);
    try {
      const company = existingCompanyFromCheck;

      // Pre-fill form fields from existing company data
      // Basic Information
      setValue('alias', company.companyName || '');
      setValue('alternativeName', company.alternativeName || '');
      setValue('nickname', company.nickname || '');

      // Registered Address - use shouldTouch to trigger auto-create location.
      // board2-#79 single-address entry: pre-fill only country/address/postcode
      // (the auto-create touched gate); stateId/cityId are backend-parse
      // OUTPUTS and are no longer set here.
      const currentCountry = getValues('country');
      if (currentCountry) {
        // Country was committed before the check ran — re-set it with
        // shouldTouch so the auto-create gate sees it after pre-fill
        setValue('country', currentCountry, { shouldTouch: true });
      }
      setValue('address', company.address || '', { shouldTouch: true });
      setValue('postCode', company.postcode || '', { shouldTouch: true });

      // Contact Details
      if (company.phoneCountryCode) {
        setValue('phoneCountryCode', company.phoneCountryCode);
      }
      if (company.phoneNumber) {
        setValue('phoneNumber', company.phoneNumber);
      }
      if (company.phoneExtension) {
        setValue('phoneExtension', company.phoneExtension);
      }
      setValue('email', company.email || '');
      setValue('fax', company.fax || '');
      setValue('web', company.web || '');

      // Clear step 0 validation errors since form is now populated
      setStepErrors((prev) => ({ ...prev, 0: false }));

      // Clear existing locations and reset the deletion flag so auto-create can work with pre-filled data
      // The reactive useEffect will automatically trigger auto-create when all conditions are met
      setValue('locations', []);
      userDeletedLocationRef.current = false;

      // Dispatch modal accepted - transitions to PRE_FILLED state with banner
      dispatchValidation({ type: 'MODAL_ACCEPTED' });
    } catch (error) {
      console.error('Error pre-filling company data:', error);
      notification.error({
        message: t('vendor.notifications.quickCreateFailed'),
        description: t('vendor.notifications.quickCreateFailedDescription'),
        placement: 'topRight',
        duration: 5,
      });
    } finally {
      setIsLoadingCompanyData(false);
    }
  };

  // Handle canceling company data modal
  const handleCancelCompanyModal = () => {
    // Dispatch modal cancelled - transitions to MANUAL_ENTRY state
    dispatchValidation({ type: 'MODAL_CANCELLED' });
    // Clear ABN field
    setValue('abn', '');
    setLocalTaxId(null);
  };

  // FSM Effect 4: Update step errors based on FSM state
  useEffect(() => {
    // Only set step error when vendor exists
    if (validationMachine.status === 'VENDOR_EXISTS') {
      setStepErrors((prev) => ({ ...prev, 0: true }));
    } else {
      setStepErrors((prev) => ({ ...prev, 0: false }));
    }
  }, [validationMachine.status]);

  // Stage 1: Initialize form with edit mode data when ready
  // Uses reset() for a single batch update (more efficient than multiple setValue calls)
  // Note: We compute form values inside the effect to avoid dependency on memoized objects
  // which could cause unnecessary re-runs due to reference changes
  useEffect(() => {
    // Guard conditions - only run once when all data is ready
    if (
      !isEditMode ||
      !existingVendor ||
      isLoadingPaymentData ||
      isLoadingLocations || // Wait for locations to load
      isLoadingContacts || // Wait for contacts to load
      isLoadingBankAccounts || // Wait for bank accounts to load
      countries.length === 0 ||
      hasLoadedEditData
    ) {
      return;
    }

    // Format contacts from API
    const formattedContacts =
      existingContacts?.map((contact) => ({
        id: `contact-${contact.contactId}`,
        name: contact.name,
        jobTitle: contact.jobTitle ?? '',
        phoneCountryCode: contact.phoneCountryCode ?? DEFAULT_COUNTRY_CODE,
        phoneNumber: contact.phoneNumber ?? '',
        phoneExtension: contact.phoneExtension ?? '',
        mobileCountryCode: contact.mobileCountryCode ?? undefined,
        mobileNumber: contact.mobileNumber ?? undefined,
        fax: contact.fax ?? '',
        email: contact.email,
        countryId: contact.countryId ?? undefined,
        stateId: contact.stateId ?? undefined,
        cityId: contact.cityId ?? undefined,
        postcode: contact.postcode ?? undefined,
        address: contact.address ?? '',
        addressLang: contact.addressLang ?? undefined,
        isPrimary: contact.isPrimary,
        isActive: contact.isActive,
      })) || [];

    // Format locations from API
    const formattedLocations =
      existingLocations?.map((location) => ({
        id: `location-${location.locationId}`,
        locationName: location.locationName,
        country: location.country,
        countryId: location.countryId,
        stateId: location.stateId,
        state: location.state,
        cityId: location.cityId,
        city: location.city,
        postcode: location.postcode,
        address: location.address,
        addressLang: location.addressLang,
        notes: location.notes || '',
        isDefaultReceiving: location.isDefaultReceiving,
        isActive: location.isActive,
      })) || [];

    // Format bank accounts from API
    const formattedBankAccounts =
      existingBankAccounts?.map((account) => ({
        vendorBankId: account.bankId, // Preserve ID for existing items
        bankName: account.bankName,
        bankCode: account.bankCode || '',
        branchCode: account.branchCode || '',
        branchName: account.branchName || '',
        countryId: account.countryId,
        bankLocation: account.countryNameEn || '',
        accountName: account.accountName,
        accountNumber: account.accountNumber,
        currency: account.currency,
        swiftCode: account.swiftCode || '',
        iban: account.iban || '',
        notes: account.notes || '',
        isDefault: account.isDefault,
        isActive: account.isActive,
      })) || [];

    // Use reset() to set all form values at once
    // This also updates defaultValues so isDirty works correctly
    reset({
      alias: existingVendor.name,
      alternativeName: existingVendor.alternativeName || '',
      nickname: existingVendor.nickname || '',
      vendorType: (existingVendor.individual
        ? 'individual'
        : 'company') as FormValues['vendorType'],
      categoryIds: existingVendor.categories?.map((c) => c.categoryId) || [],
      salesRepresentativeIds: existingVendor.salesRepresentativeIds || [],
      notes: existingVendor.notes || '',
      country: countries.find((c) => c.countryId === existingVendor.countryId)?.countryCode || '',
      // Note: stateId and cityId are set in Stage 2/3 after cascading data loads
      stateId: null,
      state: '',
      cityId: null,
      citySub: '',
      address: existingVendor.address || '',
      postCode: existingVendor.postcode || '',
      primaryPort: existingVendor.primaryPort || '',
      // Use phoneCountryCode directly from API
      phoneCountryCode: existingVendor.phoneCountryCode || DEFAULT_COUNTRY_CODE,
      phoneNumber: existingVendor.phoneNumber || '',
      phoneExtension: existingVendor.phoneExtension || '',
      mobile: existingVendor.mobile || '',
      email: existingVendor.email || '',
      web: existingVendor.web || '',
      fax: existingVendor.fax || '',
      contacts: formattedContacts,
      locations: formattedLocations,
      cashDiscountRate: existingPaymentData?.paymentTerm?.customDiscountRate || 0,
      cashDiscountDays: existingPaymentData?.paymentTerm?.customDiscountDays || 0,
      termsTypeId: existingPaymentData?.paymentTerm?.basePaymentTermId || 1,
      termsDays: existingPaymentData?.paymentTerm?.customDueDays || 0,
      primaryCurrency: existingPaymentData?.primaryCurrency || 'USD',
      abn: existingVendor.localTaxId || '',
      taxTypeId: existingPaymentData?.taxTypeId || 1,
      taxRate: existingPaymentData?.taxRate || 0,
      bankAccounts: formattedBankAccounts,
    });

    // Mark form as initialized
    setHasLoadedEditData(true);
    setIsFormInitialized(true);
  }, [
    isEditMode,
    existingVendor,
    isLoadingPaymentData,
    isLoadingLocations,
    isLoadingContacts,
    isLoadingBankAccounts,
    existingPaymentData,
    existingContacts,
    existingLocations,
    existingBankAccounts,
    countries,
    hasLoadedEditData,
    reset,
  ]);

  // board2-#79: the former Stage 2/3 effects (pre-filling stateId/cityId into
  // the form after states/cities loaded) are REMOVED — state/city are
  // backend-parse OUTPUTS. Not sending explicit ids on save lets the backend
  // (and the mock parse) re-split the merged country/postcode/address values.

  // Postcode lookup handler - auto-fill state and city parse outputs
  const lookupPostcode = useCallback(
    async (postcode: string) => {
      if (!selectedCountry || !postcode || postcode.length < 3) return;

      try {
        const response = await locationApi.lookupByPostcode({
          countryCode: selectedCountry,
          postcode: postcode,
        });

        if (response.data && response.data.length > 0) {
          const location = response.data[0];

          // Auto-fill state - mark as touched so auto-create location can trigger
          setValue('stateId', location.stateId, { shouldTouch: true });
          setValue('state', location.stateName, { shouldTouch: true });

          // Auto-fill city - mark as touched so auto-create location can trigger
          setValue('cityId', location.cityId, { shouldTouch: true });
          setValue('citySub', location.cityName, { shouldTouch: true });

          notification.success({
            message: t('vendor.notifications.locationFound'),
            description: t('vendor.notifications.locationAutoFilled', {
              state: location.stateName,
              city: location.cityName,
            }),
            placement: 'topRight',
            duration: 2,
          });
        }
      } catch (error) {
        console.error('Postcode lookup failed:', error);
        // Silently fail - user can still select manually
      }
    },
    [selectedCountry, setValue, notification, t]
  );

  // Debounced postcode lookup - triggers 500ms after user stops typing
  const { run: debouncedPostcodeLookup } = useDebounceFn(lookupPostcode, {
    wait: 500,
  });

  // Initialize default payment term when payment terms load
  useEffect(() => {
    if (paymentTerms.length > 0 && !termsTypeId) {
      // Set to first payment term by default (don't mark as dirty)
      setValue('termsTypeId', paymentTerms[0].paymentTermId, { shouldDirty: false });
    }
  }, [paymentTerms, termsTypeId, setValue]);

  // Initialize default tax type when tax types load
  useEffect(() => {
    if (taxTypes.length > 0 && !taxTypeId) {
      // Set to first tax type by default (don't mark as dirty)
      setValue('taxTypeId', taxTypes[0].taxTypeId, { shouldDirty: false });
    }
  }, [taxTypes, taxTypeId, setValue]);

  // Auto-update tax rate when tax type changes
  useEffect(() => {
    const selectedTaxType = taxTypes.find((t) => t.taxTypeId === taxTypeId);
    if (selectedTaxType) {
      setValue('taxRate', selectedTaxType.taxRate, { shouldDirty: false });
    }
  }, [taxTypeId, taxTypes, setValue]);

  // Set termsDays to default dueDays from selected payment term
  useEffect(() => {
    const selectedTerm = paymentTerms.find((term) => term.paymentTermId === termsTypeId);
    if (selectedTerm) {
      if (selectedTerm.dueDays === 0) {
        // Clear for 0-day terms (COD, EOM, RECEIPT, etc.) (don't mark as dirty)
        setValue('termsDays', undefined, { shouldDirty: false });
      } else {
        // Set to default dueDays for other terms (can be customized by user) (don't mark as dirty)
        setValue('termsDays', selectedTerm.dueDays, { shouldDirty: false });
      }
    }
  }, [termsTypeId, paymentTerms, setValue]);

  // Navigation guard for browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Check selected payment term properties
  const selectedPaymentTerm = paymentTerms.find((term) => term.paymentTermId === termsTypeId);
  const isZeroDayTerm = selectedPaymentTerm?.dueDays === 0;
  const termHasDiscount = selectedPaymentTerm?.hasDiscount === true;

  const stepItems = [
    {
      title: t('vendor.tabs.primaryInfo'),
      icon: <FileTextOutlined />,
      description: t('vendor.stepDescriptions.primaryInfo'),
    },
    {
      title: t('vendor.tabs.financialInfo'),
      icon: <DollarOutlined />,
      description: t('vendor.stepDescriptions.financialInfo'),
    },
    {
      title: t('vendor.tabs.bankAccounts'),
      icon: <BankOutlined />,
      description: t('vendor.stepDescriptions.bankAccounts'),
    },
  ];

  // Step validation and navigation
  const handleStepChange = async (newStep: number) => {
    // Allow going backward without confirmation (internal step navigation)
    if (newStep < currentStep) {
      setCurrentStep(newStep);
      return;
    }

    // If going forward, validate current step first
    let fieldsToValidate: Array<keyof FormValues> = [];

    if (currentStep === 0) {
      // board2-#79: address entry = country + postcode + single address
      // textfield (stateId/cityId are backend-parse outputs, not form fields)
      fieldsToValidate = [
        'country',
        'abn',
        'vendorType',
        'alias',
        'categoryIds',
        'postCode',
        'address',
        'phoneCountryCode',
        'phoneNumber',
        'email',
      ];
    } else if (currentStep === 1) {
      fieldsToValidate = ['termsTypeId', 'primaryCurrency', 'taxTypeId'];
    }

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      // Proceed to next step
      setCurrentStep(newStep);
      setStepErrors((prev) => ({ ...prev, [currentStep]: false }));
    } else {
      setStepErrors((prev) => ({ ...prev, [currentStep]: true }));
      // Validation errors are shown inline below each field, no message needed
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      confirmUnsavedChanges(() => navigate(-1));
    } else {
      navigate(-1);
    }
  };

  // Helper function to save current step data (edit mode only)
  // Implements cumulative save pattern:
  // - Step 1: Vendor + Contacts + Locations
  // - Step 2: Vendor + Contacts + Locations + Payment
  // - Step 3: Vendor + Contacts + Locations + Payment + Bank Accounts
  const saveCurrentStep = async (): Promise<boolean> => {
    if (!isEditMode || !numericVendorId) {
      return true; // Skip saving in create mode
    }

    setIsSaving(true);
    const failedOperations: string[] = [];

    try {
      const data = getValues();

      // ========================================
      // ALWAYS: Save Vendor basic info
      // ========================================
      const vendorData = {
        name: data.alias,
        alternativeName: data.alternativeName,
        nickname: data.nickname,
        vendorCode: existingVendor?.vendorCode || 'VEN',
        localTaxId: data.abn,
        individual: data.vendorType === 'individual',
        categoryIds: data.categoryIds,
        primaryPort: data.primaryPort,
        internalContactUserId: data.salesRepresentativeIds?.[0] || undefined,
        phoneCountryCode: data.phoneCountryCode,
        phoneNumber: data.phoneNumber,
        phoneExtension: data.phoneExtension || undefined,
        email: data.email,
        fax: data.fax,
        web: data.web,
        notes: data.notes,
        ...sanitizeLocationIds({ countryId, stateId: data.stateId, cityId: data.cityId }),
        postcode: data.postCode,
        address: data.address,
        addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en', // Address language based on current UI language
        isActive: true,
      };

      try {
        await updateVendorMutation(numericVendorId, vendorData);
      } catch (error) {
        console.error('Failed to update vendor:', error);
        failedOperations.push(t('vendor.sections.basicInformation'));
      }

      // #91: contacts are no longer saved from the wizard — the Contacts
      // module owns contact create/edit. The wizard's contact list is view-only.

      // ========================================
      // ALWAYS: Save Locations (Step 1+)
      // ========================================
      if (locations.length > 0) {
        for (const location of locations) {
          try {
            const locationData = {
              locationName: location.locationName,
              ...sanitizeLocationIds({
                countryId: location.countryId,
                stateId: location.stateId,
                cityId: location.cityId,
              }),
              postcode: location.postcode,
              address: location.address,
              addressLang: location.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
              notes: location.notes,
              isDefaultReceiving: location.isDefaultReceiving,
              isActive: location.isActive !== false, // Default to active
            };

            // Check if this is a new location (ID > 1 billion = timestamp) or existing
            if (isNewItem(location.id)) {
              await vendorApi.createLocation(numericVendorId, locationData);
            } else {
              // Extract numeric ID from location.id (format: "location-123" or "location-registered-address")
              const locationIdMatch = location.id.match(/\d+$/);
              if (locationIdMatch) {
                await vendorApi.updateLocation(
                  numericVendorId,
                  parseInt(locationIdMatch[0], 10),
                  locationData
                );
              }
            }
          } catch (error) {
            console.error('Failed to save location:', location.locationName, error);
            failedOperations.push(`${t('vendor.sections.locations')}: ${location.locationName}`);
          }
        }
      }

      // ========================================
      // Step 2+: Save Payment Terms
      // ========================================
      if (currentStep >= 1) {
        try {
          const hasDiscount = !!(data.cashDiscountRate && data.cashDiscountRate > 0);
          const paymentData = {
            basePaymentTermId: data.termsTypeId || 1,
            primaryCurrency: data.primaryCurrency || 'USD',
            taxTypeId: data.taxTypeId || 1,
            customDueDays: data.termsDays || undefined,
            // Discount fields - all three required when discount is enabled
            ...(hasDiscount && {
              customHasDiscount: true,
              customDiscountRate: data.cashDiscountRate,
              customDiscountDays: data.cashDiscountDays || 0,
            }),
          };
          await updatePaymentData(paymentData);
        } catch (error) {
          console.error('Failed to update payment data:', error);
          failedOperations.push(t('vendor.sections.paymentTerms'));
        }
      }

      // ========================================
      // Step 3: Save Bank Accounts
      // ========================================
      if (currentStep >= 2 && bankAccounts.length > 0) {
        for (const bank of bankAccounts) {
          try {
            // API requires bankCode and branchCode as separate fields
            const bankData = {
              isDefault: bank.isDefault,
              accountName: bank.accountName,
              countryId: bank.countryId,
              bankCode: bank.bankCode || '', // Required by API
              bankName: bank.bankName,
              branchCode: bank.branchCode || '',
              branchName: bank.branchName || '',
              currency: bank.currency,
              accountNumber: bank.accountNumber,
              swiftCode: bank.swiftCode || '',
              iban: bank.iban || '',
              notes: bank.notes || '',
              isActive: bank.isActive,
            };

            // Check if this bank account is new or existing
            // Existing items have vendorBankId from API, new items don't
            if (!bank.vendorBankId) {
              const newBank = await createBankAccount(bankData);
              // If setting as default, call set-default API separately
              // (create API may not handle isDefault flag)
              if (bank.isDefault && newBank?.bankId) {
                await setDefaultBankAccount(newBank.bankId, true);
              }
            } else {
              await updateBankAccount(bank.vendorBankId, bankData);
              // If setting as default, call set-default API separately
              // (update API may not handle isDefault flag)
              if (bank.isDefault) {
                await setDefaultBankAccount(bank.vendorBankId, true);
              }
            }
          } catch (error) {
            console.error('Failed to save bank account:', bank.accountName, error);
            failedOperations.push(`${t('vendor.sections.bankAccounts')}: ${bank.accountName}`);
          }
        }
      }

      // ========================================
      // Show notification and navigate (only if all succeeded)
      // ========================================
      if (failedOperations.length > 0) {
        // Partial failure - stay on wizard, show warning
        notification.warning({
          message: t('vendor.notifications.vendorUpdatedWithWarnings'),
          description: t('vendor.notifications.vendorUpdatedWithWarningsDesc', {
            failedItems: failedOperations.join(', '),
          }),
          placement: 'topRight',
          duration: 8,
        });
        return false; // Stay on wizard for user to retry
      }

      // All succeeded - navigate to detail page
      notification.success({
        message: t('vendor.notifications.vendorUpdated'),
        description: t('vendor.notifications.vendorUpdatedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });

      setHasUnsavedChanges(false);
      navigate(`/auth/vendors/${numericVendorId}`);

      return true;
    } catch (error) {
      console.error('Error saving step:', error);
      notification.error({
        message: t('vendor.messages.updateFailed'),
        description: error instanceof Error ? error.message : t('messages.failedToSave'),
        placement: 'topRight',
        duration: 6,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    await handleStepChange(currentStep + 1);
  };

  const handlePrevious = async () => {
    setCurrentStep(currentStep - 1);
  };

  const onSubmit = async (data: FormValues) => {
    // Bank accounts are optional - no validation required

    setIsSaving(true);
    try {
      // Transform form data to match API request format (VendorCreate schema)
      const vendorData = {
        name: data.alias,
        alternativeName: data.alternativeName,
        nickname: data.nickname,
        vendorCode: 'VEN', // Backend will auto-generate the full code (e.g., VEN-0001)
        localTaxId: data.abn, // ABN field maps to localTaxId
        individual: data.vendorType === 'individual', // Convert vendorType to individual boolean
        categoryIds: data.categoryIds,
        primaryPort: data.primaryPort,
        internalContactUserId: data.salesRepresentativeIds?.[0] || undefined, // Backend expects single userId, not array
        phoneCountryCode: data.phoneCountryCode,
        phoneNumber: data.phoneNumber,
        phoneExtension: data.phoneExtension || undefined,
        email: data.email,
        fax: data.fax,
        web: data.web,
        notes: data.notes,
        ...sanitizeLocationIds({ countryId, stateId: data.stateId, cityId: data.cityId }),
        postcode: data.postCode,
        address: data.address,
        addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en', // Address language based on current UI language
        isActive: true,
      };

      // Note: Payment/tax data (depositPercent, cashDiscount, termsTypeId, etc.) should be saved separately via vendor payment API

      let savedVendorId: number;

      if (isEditMode && numericVendorId) {
        // Update existing vendor
        await updateVendorMutation(numericVendorId, vendorData);
        savedVendorId = numericVendorId;

        notification.success({
          message: t('vendor.notifications.vendorUpdated'),
          description: t('vendor.notifications.vendorUpdatedDesc'),
          icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
          placement: 'topRight',
          duration: 3,
        });
      } else {
        // ========================================
        // CREATE MODE: Sequential API calls
        // ========================================

        // Step 1: Create vendor
        const response = await createVendor(vendorData);
        savedVendorId = response.vendorId;

        // Track partial failures
        const failedOperations: string[] = [];

        // Step 2: Create payment data (if exists)
        // Uses VendorPaymentCreate schema from api-spec.yaml
        if (data.termsTypeId || data.taxTypeId) {
          try {
            const hasDiscount = !!(data.cashDiscountRate && data.cashDiscountRate > 0);
            const paymentData = {
              basePaymentTermId: data.termsTypeId || 1, // Required - base payment term template ID
              primaryCurrency: data.primaryCurrency || 'USD', // Required - 3-character currency code
              taxTypeId: data.taxTypeId || 1, // Required - tax type ID
              // Deposit and Cash Discount percentages (always visible fields)
              depositPercentage: data.deposit,
              cashDiscountPercentage: data.cashDiscount,
              // Optional custom term fields
              customDueDays: data.termsDays || undefined,
              // Discount fields - all three required when discount is enabled (conditional fields based on hasDiscount)
              ...(hasDiscount && {
                customHasDiscount: true,
                customDiscountRate: data.cashDiscountRate,
                customDiscountDays: data.cashDiscountDays || 0,
              }),
            };
            await paymentApi.create(savedVendorId, paymentData);
          } catch (error) {
            console.error('Failed to save payment data:', error);
            failedOperations.push('Payment terms');
          }
        }

        // #91: the sequential contact-POST block was removed — the create flow
        // never creates contacts; they are added afterwards in the Contacts module.

        // Step 3: Create locations (if any)
        if (locations.length > 0) {
          try {
            await Promise.all(
              locations.map((location) =>
                vendorApi.createLocation(savedVendorId, {
                  locationName: location.locationName,
                  ...sanitizeLocationIds({
                    countryId: location.countryId,
                    stateId: location.stateId,
                    cityId: location.cityId,
                  }),
                  postcode: location.postcode,
                  address: location.address,
                  addressLang:
                    location.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
                  notes: location.notes,
                  isDefaultReceiving: location.isDefaultReceiving,
                  isActive: true,
                })
              )
            );
          } catch (error) {
            console.error('Failed to create locations:', error);
            failedOperations.push('Locations');
          }
        }

        // Step 4: Create bank accounts (if any)
        if (bankAccounts.length > 0) {
          try {
            await Promise.all(
              bankAccounts.map((bank) => {
                // API requires bankCode and branchCode as separate fields
                return bankApi.create(savedVendorId, {
                  isDefault: bank.isDefault,
                  accountName: bank.accountName,
                  countryId: bank.countryId,
                  bankCode: bank.bankCode || '', // Required by API
                  bankName: bank.bankName,
                  branchCode: bank.branchCode || '',
                  branchName: bank.branchName || '',
                  currency: bank.currency,
                  accountNumber: bank.accountNumber,
                  swiftCode: bank.swiftCode || '',
                  iban: bank.iban || '',
                  notes: bank.notes || '',
                  isActive: bank.isActive,
                });
              })
            );
          } catch (error) {
            console.error('Failed to create bank accounts:', error);
            failedOperations.push('Bank accounts');
          }
        }

        // Final notification
        if (failedOperations.length > 0) {
          notification.warning({
            message: t('vendor.notifications.vendorCreatedWithWarnings'),
            description: t('vendor.notifications.vendorCreatedWithWarningsDesc', {
              failedItems: failedOperations.join(', '),
            }),
            placement: 'topRight',
            duration: 8,
          });
          // Stay on wizard for user to retry failed operations
          return;
        } else {
          notification.success({
            message: t('vendor.notifications.vendorCreated'),
            description: t('vendor.notifications.vendorCreatedDesc'),
            icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
            placement: 'topRight',
            duration: 3,
          });
        }
      }

      setHasUnsavedChanges(false);

      // Navigate to vendor detail page (only if no failures)
      navigate(`/auth/vendors/${savedVendorId}`);
    } catch (error) {
      console.error('Error saving vendor:', error);

      // Show error in notification
      const errorMessage = error instanceof Error ? error.message : t('messages.failedToSave');

      notification.error({
        message: isEditMode ? t('vendor.messages.updateFailed') : t('vendor.messages.createFailed'),
        description: errorMessage,
        placement: 'topRight',
        duration: 6,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // #91: all wizard contact handlers (add/edit/set-primary/delete/toggle) were
  // removed — contact create/edit lives exclusively in the Contacts module.

  // Location Handlers
  const handleAddLocation = (locationData: LocationFormData) => {
    const wantsToSetDefault = locationData.isDefaultReceiving === true;

    // Check if there's already a default location
    const currentDefaultIndex = locations.findIndex((l) => l.isDefaultReceiving);
    const hasExistingDefault = currentDefaultIndex !== -1;
    const isEditingCurrentDefault = editingLocation?.id === locations[currentDefaultIndex]?.id;

    // If user wants to set this as default and there's already a different default location
    if (wantsToSetDefault && hasExistingDefault && !isEditingCurrentDefault) {
      modal.confirm({
        title: t('vendor.notifications.defaultLocationUpdated'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: (
          <Text>
            {t('common:messages.replaceDefaultLocationPrefix')}
            <Text strong>{locations[currentDefaultIndex].locationName}</Text>
            {t('common:messages.replaceDefaultLocationSuffix')}
          </Text>
        ),
        okText: t('vendor.modals.replace'),
        okType: 'primary',
        cancelText: t('vendor.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: () => {
          saveLocation(locationData, true);
          setLocationModalOpen(false);
        },
        onCancel: () => {
          // User cancelled - don't save, keep location modal open
        },
      });
    } else {
      // No conflict, save normally
      saveLocation(locationData, wantsToSetDefault);
      setLocationModalOpen(false);
    }
  };

  // Helper function to save location after default logic is resolved
  const saveLocation = (locationData: LocationFormData, isDefault: boolean) => {
    if (editingLocation) {
      // Update existing location
      const updatedLocation: VendorLocation = {
        ...editingLocation,
        isDefaultReceiving: isDefault,
        locationName: locationData.locationName,
        country: locationData.country,
        countryId: locationData.countryId,
        stateId: locationData.stateId,
        state: locationData.state,
        cityId: locationData.cityId,
        city: locationData.city,
        postcode: locationData.postcode,
        address: locationData.address,
        addressLang: locationData.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
        notes: locationData.notes || '',
      };

      // If setting this as default, remove default from all others
      const updatedLocations = locations.map((l) =>
        l.id === editingLocation.id
          ? updatedLocation
          : { ...l, isDefaultReceiving: isDefault ? false : l.isDefaultReceiving }
      );

      setValue('locations', updatedLocations, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setEditingLocation(null);
      setEditingLocationFormData(null);
      notification.success({
        message: t('vendor.notifications.locationUpdated'),
        description: t('vendor.notifications.locationUpdatedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    } else {
      // Add new location
      const newLocation: VendorLocation = {
        id: `location-${Date.now()}`,
        isDefaultReceiving: isDefault,
        locationName: locationData.locationName,
        country: locationData.country,
        countryId: locationData.countryId,
        stateId: locationData.stateId,
        state: locationData.state,
        cityId: locationData.cityId,
        city: locationData.city,
        postcode: locationData.postcode,
        address: locationData.address,
        addressLang: locationData.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
        notes: locationData.notes || '',
      };

      // If setting this as default, remove default from all existing locations and add new at top
      // Otherwise, insert new location after default or at top if no default
      let updatedLocations: VendorLocation[];
      if (isDefault) {
        updatedLocations = [
          newLocation,
          ...locations.map((l) => ({ ...l, isDefaultReceiving: false })),
        ];
      } else {
        // Find default index and insert after it
        const defaultIndex = locations.findIndex((l) => l.isDefaultReceiving);
        if (defaultIndex !== -1) {
          updatedLocations = [
            ...locations.slice(0, defaultIndex + 1),
            newLocation,
            ...locations.slice(defaultIndex + 1),
          ];
        } else {
          // No default, add at top
          updatedLocations = [newLocation, ...locations];
        }
      }

      setValue('locations', updatedLocations, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      notification.success({
        message: t('vendor.notifications.locationAdded'),
        description: t('vendor.notifications.locationAddedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    }

    setHasUnsavedChanges(true);
  };

  const handleEditLocation = (location: VendorLocation) => {
    setEditingLocation(location);

    // Extract the numeric location ID from the string ID (e.g., "location-123" -> 123)
    const locationIdMatch = location.id.match(/\d+$/);
    const isExistingLocation = locationIdMatch && !isNewItem(location.id);

    if (isEditMode && isExistingLocation) {
      // For existing locations in edit mode, fetch from API to get raw address data
      setEditingLocationId(parseInt(locationIdMatch[0], 10));
      setEditingLocationFormData(null); // Will be set by useMemo when API returns
    } else {
      // For new locations (create mode or newly added in edit mode), use local data
      setEditingLocationId(null);
      const formData: LocationFormData = {
        locationName: location.locationName,
        isDefaultReceiving: location.isDefaultReceiving,
        country: location.country,
        countryId: location.countryId ?? null,
        stateId: location.stateId,
        state: location.state,
        cityId: location.cityId,
        city: location.city,
        postcode: location.postcode,
        address: location.address,
        addressLang: location.addressLang ?? null,
        notes: location.notes || '',
      };
      setEditingLocationFormData(formData);
    }

    setLocationModalOpen(true);
  };

  const handleSetAsDefaultLocation = (locationId: string) => {
    const currentDefault = locations.findIndex((l) => l.isDefaultReceiving);
    const locationIndex = locations.findIndex((l) => l.id === locationId);

    // Helper function to set default (called after confirmation if needed)
    // Only updates local state - API call happens when user clicks Save
    const setAsDefault = () => {
      // Update local state
      const updatedLocations = locations.map((location) => ({
        ...location,
        isDefaultReceiving: location.id === locationId,
      }));
      setValue('locations', updatedLocations, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      notification.success({
        message: t('vendor.notifications.defaultLocationUpdated'),
        description: t('vendor.notifications.defaultLocationSet'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    };

    if (currentDefault !== -1 && currentDefault !== locationIndex) {
      const currentDefaultLocation = locations[currentDefault];

      // Confirm replacing existing default
      modal.confirm({
        title: t('vendor.notifications.defaultLocationUpdated'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: (
          <Text>
            {t('common:messages.replaceDefaultLocationPrefix')}
            <Text strong>{currentDefaultLocation.locationName}</Text>
            {t('common:messages.replaceDefaultLocationSuffix')}
          </Text>
        ),
        okText: t('vendor.modals.replace'),
        okType: 'primary',
        cancelText: t('vendor.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: setAsDefault,
      });
    } else {
      // No existing default or same location - just set it
      setAsDefault();
    }
  };

  const handleDeleteLocation = (locationId: string) => {
    const location = locations.find((l) => l.id === locationId);

    // Check if the location is the default
    if (location?.isDefaultReceiving) {
      notification.error({
        message: t('vendor.notifications.cannotDeleteDefaultLocation'),
        description: t('vendor.notifications.cannotDeleteDefaultLocationDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    modal.confirm({
      title: t('vendor.modals.deleteLocation'),
      icon: <ExclamationCircleOutlined style={{ color: token.colorError }} />,
      content: 'Are you sure you want to delete this location?',
      okText: t('vendor.buttons.delete'),
      okType: 'danger',
      cancelText: t('vendor.actions.cancel'),
      okButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      cancelButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      onOk: () => {
        const updatedLocations = locations.filter((l) => l.id !== locationId);
        setValue('locations', updatedLocations, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        setHasUnsavedChanges(true);
        // Mark that user has manually deleted a location to prevent auto-create from re-adding
        userDeletedLocationRef.current = true;
        notification.success({
          message: t('vendor.notifications.locationDeleted'),
          description: t('vendor.notifications.locationDeletedDesc'),
          icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
          placement: 'topRight',
          duration: 3,
        });
      },
    });
  };

  // Toggle location status (edit mode only)
  const handleToggleLocationStatus = async (locationId: string) => {
    const location = locations.find((l) => l.id === locationId);
    const isCurrentlyActive = location?.isActive !== false;

    // Check if trying to deactivate a default location
    if (location?.isDefaultReceiving && isCurrentlyActive) {
      notification.error({
        message: t('vendor.notifications.cannotDeactivateDefault'),
        description: t('vendor.notifications.cannotDeactivateDefaultDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    modal.confirm({
      title: isCurrentlyActive
        ? t('vendor.modals.deactivateLocationTitle')
        : t('vendor.modals.activateLocationTitle'),
      icon: <ExclamationCircleOutlined style={{ color: token.colorWarning }} />,
      content: isCurrentlyActive
        ? t('vendor.modals.deactivateLocationConfirm')
        : t('vendor.modals.activateLocationConfirm'),
      okText: isCurrentlyActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate'),
      okType: isCurrentlyActive ? 'danger' : 'primary',
      cancelText: t('vendor.actions.cancel'),
      okButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      cancelButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      onOk: async () => {
        try {
          // For existing locations (from API), call the toggle API
          // For new locations (not yet saved), just update local state
          const locationIdMatch = locationId.match(/\d+$/);
          const isExistingLocation = locationIdMatch && !isNewItem(locationId);

          if (isEditMode && isExistingLocation && numericVendorId) {
            // Call API to toggle the location status
            await locationMutations.toggleStatus(
              parseInt(locationIdMatch[0], 10),
              !isCurrentlyActive
            );
          }

          // Update local state
          const updatedLocations = locations.map((l) =>
            l.id === locationId ? { ...l, isActive: !isCurrentlyActive } : l
          );
          setValue('locations', updatedLocations, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });

          notification.success({
            message: isCurrentlyActive
              ? t('vendor.notifications.locationDeactivated')
              : t('vendor.notifications.locationActivated'),
            description: isCurrentlyActive
              ? t('vendor.notifications.locationDeactivatedDesc')
              : t('vendor.notifications.locationActivatedDesc'),
            icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
            placement: 'topRight',
            duration: 3,
          });
        } catch (error) {
          console.error('Error toggling location status:', error);
          notification.error({
            message: t('vendor.messages.locationStatusUpdateFailed'),
            placement: 'topRight',
            duration: 4,
          });
        }
      },
    });
  };

  // Bank Account Handlers
  const handleAddBankAccount = () => {
    setEditingBankAccountIndex(null);
    setBankAccountReadOnly(false);
    setBankAccountModalOpen(true);
  };

  const handleEditBankAccount = (index: number) => {
    setEditingBankAccountIndex(index);
    setBankAccountReadOnly(false);
    setBankAccountModalOpen(true);
  };

  const handleViewBankAccount = (index: number) => {
    setEditingBankAccountIndex(index);
    setBankAccountReadOnly(true);
    setBankAccountModalOpen(true);
  };

  const handleSaveBankAccount = (bankAccountData: BankAccountFormInput) => {
    const wantsToSetDefault = bankAccountData.isDefault === true;

    // Check if there's already a default bank account
    const currentDefaultIndex = bankAccounts.findIndex((b) => b.isDefault);
    const hasExistingDefault = currentDefaultIndex !== -1;
    const isEditingCurrentDefault = editingBankAccountIndex === currentDefaultIndex;

    // If user wants to set this as default and there's already a different default
    if (wantsToSetDefault && hasExistingDefault && !isEditingCurrentDefault) {
      modal.confirm({
        title: t('vendor.modals.defaultBankAccountExists'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: t('vendor.modals.replaceDefaultBankAccount', {
          current: bankAccounts[currentDefaultIndex].accountName,
          new: bankAccountData.accountName,
        }),
        okText: t('vendor.modals.replaceDefault'),
        okType: 'primary',
        cancelText: t('vendor.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: () => {
          saveBankAccount(bankAccountData, true); // Save with isDefault = true
          handleCloseBankAccountModal();
        },
        onCancel: () => {
          // User cancelled - don't save, keep modal open
        },
      });
    } else {
      // No conflict, save normally
      saveBankAccount(bankAccountData, wantsToSetDefault);
      handleCloseBankAccountModal();
    }
  };

  // Helper function to save bank account after default logic is resolved
  const saveBankAccount = (bankAccountData: BankAccountFormInput, isDefault: boolean) => {
    const currentBankAccounts = [...bankAccounts];

    if (editingBankAccountIndex !== null) {
      // Editing existing - update the bank account and handle default flag
      // Preserve vendorBankId from original record (for existing API items)
      const existingAccount = bankAccounts[editingBankAccountIndex];
      currentBankAccounts[editingBankAccountIndex] = {
        ...bankAccountData,
        vendorBankId: existingAccount?.vendorBankId, // Preserve ID for API update
        isDefault,
      };

      // If setting this as default, remove default from all others
      if (isDefault) {
        currentBankAccounts.forEach((acc, idx) => {
          if (idx !== editingBankAccountIndex) {
            acc.isDefault = false;
          }
        });
      }

      notification.success({
        message: t('vendor.notifications.bankAccountUpdated'),
        description: t('vendor.notifications.bankAccountUpdatedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    } else {
      // Adding new - insert after default or at top if no default
      const newAccount = {
        ...bankAccountData,
        isDefault,
      };

      if (isDefault) {
        // If setting as default, remove default from all others and add at top
        currentBankAccounts.forEach((acc) => {
          acc.isDefault = false;
        });
        currentBankAccounts.unshift(newAccount);
      } else {
        // Find default index and insert after it
        const defaultIndex = currentBankAccounts.findIndex((acc) => acc.isDefault);
        if (defaultIndex !== -1) {
          currentBankAccounts.splice(defaultIndex + 1, 0, newAccount);
        } else {
          // No default, add at top
          currentBankAccounts.unshift(newAccount);
        }
      }

      notification.success({
        message: t('vendor.notifications.bankAccountAdded'),
        description: t('vendor.notifications.bankAccountAddedDesc'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    }

    setValue('bankAccounts', currentBankAccounts, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setHasUnsavedChanges(true);
  };

  const handleSetAsDefaultBankAccount = (index: number) => {
    const currentDefault = bankAccounts.findIndex((acc) => acc.isDefault);

    if (currentDefault !== -1 && currentDefault !== index) {
      // Confirm replacing existing default
      modal.confirm({
        title: t('vendor.modals.defaultAccountExists'),
        icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
        content: t('vendor.modals.setAsDefaultConfirm'),
        okText: t('vendor.modals.setAsDefault'),
        okType: 'primary',
        cancelText: t('vendor.actions.cancel'),
        okButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        cancelButtonProps: {
          style: { height: `${controlHeightTokens.controlHeight}px` },
        },
        onOk: () => {
          const updatedAccounts = bankAccounts.map((acc, idx) => ({
            ...acc,
            isDefault: idx === index,
          }));
          setValue('bankAccounts', updatedAccounts, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setHasUnsavedChanges(true);
          notification.success({
            message: t('vendor.notifications.defaultAccountUpdated'),
            description: t('vendor.notifications.defaultAccountUpdatedDesc'),
            icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
            placement: 'topRight',
            duration: 3,
          });
        },
      });
    } else {
      // No existing default, just set it
      const updatedAccounts = bankAccounts.map((acc, idx) => ({
        ...acc,
        isDefault: idx === index,
      }));
      setValue('bankAccounts', updatedAccounts, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setHasUnsavedChanges(true);
      notification.success({
        message: t('vendor.notifications.defaultAccountUpdated'),
        description: t('vendor.notifications.defaultAccountSet'),
        icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        placement: 'topRight',
        duration: 3,
      });
    }
  };

  const handleDeleteBankAccount = (index: number) => {
    // Check if the account is the default
    if (bankAccounts[index].isDefault) {
      notification.error({
        message: t('vendor.notifications.cannotDeleteDefault'),
        description: t('vendor.notifications.cannotDeleteDefaultDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    modal.confirm({
      title: t('vendor.modals.deleteBankAccount'),
      icon: <ExclamationCircleOutlined style={{ color: token.colorError }} />,
      content: t('vendor.modals.deleteBankAccountConfirm'),
      okText: t('vendor.buttons.delete'),
      okType: 'danger',
      cancelText: t('vendor.actions.cancel'),
      okButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      cancelButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      onOk: () => {
        const updatedAccounts = bankAccounts.filter((_, idx) => idx !== index);
        setValue('bankAccounts', updatedAccounts, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        setHasUnsavedChanges(true);

        notification.success({
          message: t('vendor.notifications.bankAccountDeleted'),
          description: t('vendor.notifications.bankAccountDeletedDesc'),
          icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
          placement: 'topRight',
          duration: 3,
        });
      },
    });
  };

  // Toggle bank account status (edit mode only - calls API)
  const handleToggleBankAccountStatus = async (index: number) => {
    const bankAccount = bankAccounts[index];
    const isCurrentlyActive = bankAccount.isActive !== false;

    // Check if trying to deactivate a default account
    if (bankAccount.isDefault && isCurrentlyActive) {
      notification.error({
        message: t('vendor.notifications.cannotDeactivateDefault'),
        description: t('vendor.notifications.cannotDeactivateDefaultDesc'),
        placement: 'topRight',
        duration: 4,
      });
      return;
    }

    const vendorBankId = bankAccount.vendorBankId;
    if (!vendorBankId) {
      // No vendorBankId means it's a new item - should use delete instead
      handleDeleteBankAccount(index);
      return;
    }

    modal.confirm({
      title: isCurrentlyActive
        ? t('vendor.modals.deactivateBankAccountTitle')
        : t('vendor.modals.activateBankAccountTitle'),
      icon: <ExclamationCircleOutlined style={{ color: token.colorWarning }} />,
      content: isCurrentlyActive
        ? t('vendor.modals.deactivateBankAccountConfirm')
        : t('vendor.modals.activateBankAccountConfirm'),
      okText: isCurrentlyActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate'),
      okType: isCurrentlyActive ? 'danger' : 'primary',
      cancelText: t('vendor.actions.cancel'),
      okButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      cancelButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      onOk: async () => {
        try {
          // Call API to toggle the bank account status
          await bankApi.toggleStatus(numericVendorId!, vendorBankId, !isCurrentlyActive);

          // Update local state with new status
          const updatedAccounts = bankAccounts.map((acc, idx) =>
            idx === index ? { ...acc, isActive: !isCurrentlyActive } : acc
          );
          setValue('bankAccounts', updatedAccounts, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });

          notification.success({
            message: isCurrentlyActive
              ? t('vendor.notifications.bankAccountDeactivated')
              : t('vendor.notifications.bankAccountActivated'),
            description: isCurrentlyActive
              ? t('vendor.notifications.bankAccountDeactivatedDesc')
              : t('vendor.notifications.bankAccountActivatedDesc'),
            icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
            placement: 'topRight',
            duration: 3,
          });
        } catch (error) {
          console.error('Error toggling bank account status:', error);
          notification.error({
            message: isCurrentlyActive
              ? t('vendor.notifications.bankAccountDeactivateFailed')
              : t('vendor.notifications.bankAccountActivateFailed'),
            description: error instanceof Error ? error.message : t('messages.failedToSave'),
            placement: 'topRight',
            duration: 4,
          });
        }
      },
    });
  };

  const handleCloseBankAccountModal = () => {
    setBankAccountModalOpen(false);
    setEditingBankAccountIndex(null);
    setBankAccountReadOnly(false);
  };

  // Calculate heights
  const NAVBAR_HEIGHT = 48; // MainLayout navbar height (Ant Design default)
  const FOOTER_HEIGHT = 80; // Sticky bottom buttons

  // Standardized styles
  const largeButtonStyle = {
    height: `${controlHeightTokens.controlHeightXL}px`,
  };

  // Override formStyles with generous spacing (wizard uses more generous spacing than modals)
  const fieldContainerStyle = {
    ...formStyles.fieldContainerStyle,
    marginBottom: '32px',
    padding: '20px',
  };

  // Use standardized form styles
  const sectionTitleStyle = formStyles.sectionTitleStyle;

  const labelStyle = {
    ...formStyles.labelStyle,
    color: token.colorTextSecondary,
    marginBottom: '4px',
  };

  // Show loading spinner when loading vendor data in edit mode
  if (
    isEditMode &&
    (isLoadingVendor ||
      isLoadingContacts ||
      isLoadingLocations ||
      isLoadingBankAccounts ||
      isLoadingPaymentData)
  ) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-96">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {/* Global styles for dropdown items to match input height */}
      <style>
        {`
          /* Center all large select placeholders vertically */
          .ant-select-lg .ant-select-selection-placeholder {
            position: absolute !important;
            top: 0 !important;
            bottom: 0 !important;
            inset-inline-start: 11px !important;
            inset-inline-end: 11px !important;
            transform: none !important;
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
          }

          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
          .ant-message-notice-content {
            min-height: ${controlHeightTokens.controlHeight}px !important;
            padding: 8px 16px !important;
            display: flex !important;
            align-items: center !important;
          }

          /* Category multi-select styling */
          .category-select-standard .ant-select-selector {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 4px 8px !important;
            border-radius: ${radiusTokens.card}px !important;
          }

          /* +N overflow tag styling */
          .category-select-standard .ant-select-selection-overflow-item-suffix .ant-select-selection-item {
            background-color: ${token.colorFillQuaternary} !important;
            color: ${token.colorTextSecondary} !important;
            border: none !important;
          }

          /* Category dropdown styling */
          .category-dropdown-elevated {
            background-color: ${token.colorBgElevated} !important;
            border: 1px solid ${token.colorBorderSecondary} !important;
            border-radius: ${radiusTokens.panel}px !important;
            box-shadow: ${token.boxShadowSecondary} !important;
          }

          .category-dropdown-elevated .ant-select-item {
            padding: 8px 12px !important;
            height: ${controlHeightTokens.controlHeightXL}px !important;
            display: flex !important;
            align-items: center !important;
          }

          .category-dropdown-elevated .ant-select-item .ant-select-item-option-content {
            height: ${controlHeightTokens.controlHeightXL}px !important;
            display: flex !important;
            align-items: center !important;
          }

          /* Sales Rep Select Styling (Match Category Select) */
          .sales-rep-select .ant-select-selector {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 4px 8px !important;
            display: flex !important;
            align-items: center !important;
          }

          .sales-rep-select .ant-select-selection-placeholder {
            position: absolute !important;
            top: 0 !important;
            bottom: 0 !important;
            inset-inline-start: 11px !important;
            inset-inline-end: 11px !important;
            transform: none !important;
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
          }
        `}
      </style>
      <Layout
        style={{
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          backgroundColor: token.colorBgLayout,
          borderTop: `1px solid ${token.colorBorder}`,
        }}
      >
        {/* Sticky Sidebar with Steps */}
        <Sider
          width={300}
          style={{
            backgroundColor: token.colorBgContainer,
            borderRight: `1px solid ${token.colorBorderSecondary}`,
            height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
            position: 'sticky',
            top: `${NAVBAR_HEIGHT}px`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: token.paddingLG,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: `${FOOTER_HEIGHT}px`, // Reserve space for footer alignment
              position: 'relative', // For absolute positioning of progress section
            }}
          >
            {/* Steps */}
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: token.paddingMD }}>
              {stepItems.map((step, index) => {
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;
                const hasError = stepErrors[index];

                return (
                  <React.Fragment key={index}>
                    <div
                      onClick={() => handleStepChange(index)}
                      style={{
                        padding: token.paddingLG,
                        marginBottom: token.marginMD,
                        borderRadius: radiusTokens.panel,
                        backgroundColor: hasError
                          ? token.colorErrorBg
                          : isCurrent
                            ? token.colorPrimaryBg
                            : isCompleted
                              ? token.colorSuccessBg
                              : token.colorFillQuaternary,
                        border: `2px solid ${
                          hasError
                            ? token.colorError
                            : isCurrent
                              ? token.colorPrimary
                              : isCompleted
                                ? token.colorSuccess
                                : 'transparent'
                        }`,
                        cursor: 'pointer',
                        position: 'relative',
                      }}
                    >
                      {/* Status icons in top-right */}
                      {isCompleted && (
                        <div style={{ position: 'absolute', top: 12, right: 12 }}>
                          <CheckCircleOutlined
                            style={{
                              fontSize: 20,
                              color: token.colorSuccess,
                            }}
                          />
                        </div>
                      )}

                      {!isCompleted && !isCurrent && (
                        <div style={{ position: 'absolute', top: 12, right: 12 }}>
                          <ClockCircleOutlined
                            style={{
                              fontSize: 20,
                              color: token.colorTextDisabled,
                            }}
                          />
                        </div>
                      )}

                      <div className="tw-flex tw-items-start tw-gap-4">
                        {/* Large Icon */}
                        <div
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: radiusTokens.panel,
                            backgroundColor: hasError
                              ? token.colorError
                              : isCompleted
                                ? token.colorSuccess
                                : isCurrent
                                  ? token.colorPrimary
                                  : token.colorFillTertiary,
                            color:
                              hasError || isCompleted || isCurrent
                                ? token.colorWhite
                                : token.colorTextDisabled,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: 24,
                          }}
                        >
                          {step.icon}
                        </div>

                        {/* Step Info */}
                        <div style={{ flex: 1, paddingTop: 2 }}>
                          <div
                            style={{
                              fontSize: token.fontSizeSM,
                              color: token.colorTextSecondary,
                              marginBottom: 4,
                              textTransform: 'uppercase',
                              fontWeight: token.fontWeightStrong,
                              letterSpacing: '0.5px',
                            }}
                          >
                            Step {index + 1}
                          </div>
                          <div
                            style={{
                              fontSize: token.fontSize,
                              fontWeight: token.fontWeightStrong,
                              color: hasError
                                ? token.colorError
                                : isCurrent
                                  ? token.colorPrimary
                                  : isCompleted
                                    ? token.colorSuccess
                                    : token.colorText,
                              marginBottom: 4,
                            }}
                          >
                            {step.title}
                          </div>
                          <div
                            style={{
                              fontSize: token.fontSizeSM,
                              color: token.colorTextSecondary,
                            }}
                          >
                            {step.description}
                          </div>

                          {/* Status Badge */}
                          <div style={{ marginTop: 8 }}>
                            <Badge
                              status={hasError ? 'error' : isCompleted ? 'success' : 'default'}
                              text={
                                <Text
                                  style={{
                                    fontSize: token.fontSizeSM,
                                    color: hasError
                                      ? token.colorError
                                      : isCompleted
                                        ? token.colorSuccess
                                        : isCurrent
                                          ? token.colorPrimary
                                          : token.colorTextDisabled,
                                  }}
                                >
                                  {hasError
                                    ? t('vendor.wizard.status.hasErrors')
                                    : isCompleted
                                      ? t('vendor.wizard.status.completed')
                                      : isCurrent
                                        ? t('vendor.wizard.status.inProgress')
                                        : t('vendor.wizard.status.pending')}
                                </Text>
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Progress Info - Footer structure */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: `${FOOTER_HEIGHT}px`,
                padding: `${token.paddingMD}px ${token.paddingLG}px`,
                backgroundColor: token.colorBgContainer,
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                zIndex: 98,
              }}
            >
              <Text
                style={{
                  display: 'block',
                  fontSize: token.fontSizeSM,
                  color: token.colorTextSecondary,
                  textAlign: 'center',
                }}
              >
                Step {currentStep + 1} of {stepItems.length}
              </Text>
              <div
                style={{
                  width: '100%',
                  height: 4,
                  backgroundColor: token.colorBgTextHover,
                  borderRadius: 2,
                  marginTop: token.marginSM,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${((currentStep + 1) / stepItems.length) * 100}%`,
                    height: '100%',
                    backgroundColor: token.colorPrimary,
                  }}
                />
              </div>
            </div>
          </div>
        </Sider>

        {/* Scrollable Content Area */}
        <Content
          style={{
            backgroundColor: token.colorBgLayout,
            position: 'relative',
            height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
            overflow: 'hidden',
            boxShadow: 'inset 0 4px 8px rgba(0, 0, 0, 0.06), -4px 0 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div
            style={{
              height: `calc(100vh - ${NAVBAR_HEIGHT + FOOTER_HEIGHT}px)`,
              overflowY: 'auto',
              padding: token.paddingLG,
            }}
          >
            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
              <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
                {/* Create-mode reminder banner (#89) — CREATE only, never edit */}
                {!isEditMode && (
                  <Alert
                    type="info"
                    showIcon
                    message={t('vendor.wizard.createReminder')}
                    style={{ marginBottom: '16px' }}
                  />
                )}
                {/* Step 1: Primary Information */}
                {currentStep === 0 && (
                  <div>
                    {/* Warning Banner for Pre-Filled Data */}
                    {validationMachine.showWarningBanner && (
                      <Alert
                        message={t('vendor.warnings.editingExistingCompanyTitle')}
                        description={t('vendor.warnings.editingExistingCompanyDescription', {
                          taxId: validationMachine.localTaxId,
                        })}
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        style={{ marginBottom: '24px' }}
                        closable
                        onClose={() => dispatchValidation({ type: 'RESET' })}
                      />
                    )}

                    {/* Basic Information Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('vendor.sections.basicInformation')}
                      </Text>
                      {/* Row 1: Country + Tax ID (ABN) */}
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.country')}
                          </Text>
                          <Controller
                            name="country"
                            control={control}
                            rules={{ required: t('vendor.validation.countryRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  value={field.value}
                                  size="large"
                                  showSearch
                                  placeholder={t('vendor.placeholders.country')}
                                  loading={isLoadingCountries}
                                  options={countries?.map((c) => ({
                                    label: c.countryName,
                                    value: c.countryCode,
                                  }))}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    field.onBlur(); // Mark as touched for auto-create location
                                    handleCountryChange(value);
                                    trigger('country'); // Re-validate to clear error
                                  }}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.country ? token.colorError : undefined,
                                  }}
                                />
                                {errors.country && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.country.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.taxIdAbn')}
                          </Text>
                          <Controller
                            name="abn"
                            control={control}
                            rules={{
                              required: t('vendor.validation.taxIdRequired'),
                              minLength: {
                                value: 8,
                                message: t('vendor.validation.taxIdMin'),
                              },
                            }}
                            render={({ field }) => (
                              <>
                                <div style={{ position: 'relative' }}>
                                  <Input
                                    {...field}
                                    size="large"
                                    placeholder="12345678"
                                    readOnly={isEditMode}
                                    onChange={(e) => {
                                      if (isEditMode) return;
                                      // Only allow numeric characters
                                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                      field.onChange(numericValue);
                                    }}
                                    style={{
                                      ...formStyles.inputStyle,
                                      fontFamily: 'monospace',
                                      borderColor: errors.abn
                                        ? token.colorError
                                        : validationMachine.status === 'VENDOR_EXISTS'
                                          ? token.colorError
                                          : validationMachine.status === 'COMPANY_FOUND'
                                            ? token.colorSuccess
                                            : undefined,
                                      paddingRight: '32px', // Space for icon
                                    }}
                                  />
                                  {/* Validation Status Icons */}
                                  {validationMachine.status === 'VALIDATING' && (
                                    <LoadingOutlined
                                      style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: token.colorTextSecondary,
                                        fontSize: '16px',
                                      }}
                                    />
                                  )}
                                  {validationMachine.status === 'VENDOR_EXISTS' && (
                                    <ExclamationCircleOutlined
                                      style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: token.colorError,
                                        fontSize: '16px',
                                      }}
                                    />
                                  )}
                                  {validationMachine.status === 'COMPANY_FOUND' && (
                                    <CheckCircleOutlined
                                      style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: token.colorSuccess,
                                        fontSize: '16px',
                                      }}
                                    />
                                  )}
                                </div>
                                {/* Validation Messages */}
                                {/*
                                  Best Practice: Show form validation errors only after field is touched (blurred)
                                  - mode: 'onBlur' ensures validation happens after blur
                                  - touchedFields check ensures we only show errors after user interaction
                                  - This prevents "required" error from showing while user is typing
                                */}
                                {errors.abn && touchedFields.abn && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.abn.message}
                                  </Text>
                                )}
                                {!errors.abn && validationMachine.status === 'VENDOR_EXISTS' && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {validationMachine.companyName
                                      ? t('vendor.validation.taxIdExistsWithName', {
                                          vendorName: validationMachine.companyName,
                                        })
                                      : t('vendor.validation.taxIdExists')}
                                  </Text>
                                )}
                                {!errors.abn && validationMachine.status === 'COMPANY_FOUND' && (
                                  <Text
                                    style={{
                                      fontSize: token.fontSizeSM,
                                      color: token.colorSuccess,
                                    }}
                                  >
                                    {t('vendor.modals.companyExistsNotVendor')}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: Vendor Type */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.type')}
                          </Text>
                          <Controller
                            name="vendorType"
                            control={control}
                            rules={{ required: 'vendor.validation.vendorTypeRequired' }}
                            render={({ field }) => (
                              <>
                                <Radio.Group {...field} size="large" style={{ marginTop: '8px' }}>
                                  <Radio value="company">{t('vendor.vendorTypes.company')}</Radio>
                                  <Radio value="individual" style={{ marginLeft: '24px' }}>
                                    {t('vendor.vendorTypes.individual')}
                                  </Radio>
                                </Radio.Group>
                                {errors.vendorType && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {t(errors.vendorType.message!)}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 3: Name (Alias) - full width */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.name')}
                          </Text>
                          <Controller
                            name="alias"
                            control={control}
                            rules={{ required: t('vendor.validation.nameRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('vendor.placeholders.vendorName')}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.alias ? token.colorError : undefined,
                                  }}
                                />
                                {errors.alias && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.alias.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 4: Alternative Name + Nickname (not required) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.alternativeName')}</Text>
                          <Controller
                            name="alternativeName"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('vendor.placeholders.alternativeName')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.nickname')}</Text>
                          <Controller
                            name="nickname"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('vendor.placeholders.nickname')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 3: Category */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.category')}
                          </Text>
                          <Controller
                            name="categoryIds"
                            control={control}
                            rules={{
                              required: t('vendor.validation.categoryRequired'),
                              validate: (value) =>
                                (value && value.length > 0) ||
                                t('vendor.validation.categoryRequired'),
                            }}
                            render={({ field, fieldState }) => (
                              <CategorySelectWithCreate
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                  trigger('categoryIds'); // Re-validate to clear error
                                }}
                                categories={categories || []}
                                loading={loadingCategories}
                                onCategoryCreated={fetchCategories}
                                error={
                                  fieldState.error?.message
                                    ? t(fieldState.error.message)
                                    : undefined
                                }
                                placeholder={t('vendor.placeholders.category')}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Location Section — board2-#79 single-address entry:
                        postcode + ONE full-address textfield (country lives in
                        Basic Information above). State/city Selects removed —
                        the backend parses the address (Google Maps) into stored
                        stateId/cityId; mocks simulate that split. */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('vendor.sections.location')}
                      </Text>
                      {/* Row 1: Postcode */}
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.postcode')}
                          </Text>
                          <Controller
                            name="postCode"
                            control={control}
                            rules={{ required: t('vendor.validation.postcodeRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('vendor.placeholders.postcode')}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    debouncedPostcodeLookup(e.target.value);
                                  }}
                                  onBlur={() => {
                                    field.onBlur();
                                  }}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.postCode ? token.colorError : undefined,
                                  }}
                                />
                                {errors.postCode && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.postCode.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: single full-address textfield (whole street
                          address in ONE field, matching the shared entry group) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.address')}
                          </Text>
                          <Controller
                            name="address"
                            control={control}
                            rules={{ required: t('vendor.validation.addressRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('vendor.placeholders.address')}
                                  onBlur={() => {
                                    field.onBlur();
                                  }}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.address ? token.colorError : undefined,
                                  }}
                                />
                                {errors.address && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.address.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Contact Information Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('vendor.sections.contactDetails')}
                      </Text>
                      {/* Row 1: Country Code + Phone Number + Extension */}
                      <Row gutter={[16, 16]}>
                        <Col span={6}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.phoneCountryCode')}
                          </Text>
                          <Controller
                            name="phoneCountryCode"
                            control={control}
                            rules={{ required: t('vendor.validation.countryCodeRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  size="large"
                                  placeholder="+1"
                                  showSearch
                                  loading={isLoadingCountries}
                                  options={countries
                                    ?.filter((c) => c.phoneCode)
                                    .map((c) => ({
                                      label: `${c.phoneCode} ${c.countryName}`,
                                      value: c.phoneCode,
                                    }))}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    trigger('phoneCountryCode'); // Re-validate to clear error
                                  }}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.phoneCountryCode
                                      ? token.colorError
                                      : undefined,
                                  }}
                                />
                                {errors.phoneCountryCode && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.phoneCountryCode.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={14}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.phoneNumberLabel')}
                          </Text>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{ required: t('vendor.validation.phoneRequired') }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  placeholder={t('vendor.placeholders.phone')}
                                  onChange={(e) => {
                                    // Only allow numeric characters
                                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                    field.onChange(numericValue);
                                  }}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.phoneNumber ? token.colorError : undefined,
                                  }}
                                />
                                {errors.phoneNumber && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.phoneNumber.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={4}>
                          <Text style={labelStyle}>{t('vendor.fields.ext')}</Text>
                          <Controller
                            name="phoneExtension"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('vendor.placeholders.ext')}
                                onChange={(e) => {
                                  // Only allow numeric characters
                                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                  field.onChange(numericValue);
                                }}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: Email (full width) */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.email')}
                          </Text>
                          <Controller
                            name="email"
                            control={control}
                            rules={{
                              required: t('vendor.validation.emailRequired'),
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('vendor.validation.emailInvalidFormat'),
                              },
                            }}
                            render={({ field }) => (
                              <>
                                <Input
                                  {...field}
                                  size="large"
                                  type="email"
                                  placeholder={t('vendor.placeholders.email')}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.email ? token.colorError : undefined,
                                  }}
                                />
                                {errors.email && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.email.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 3: Web + Fax */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.website')}</Text>
                          <Controller
                            name="web"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder="https://"
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.fax')}</Text>
                          <Controller
                            name="fax"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                placeholder={t('vendor.placeholders.fax')}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Additional Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('vendor.sections.additional')}
                      </Text>
                      {/* Row 1: Sales Rep + Primary Port */}
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.salesRep')}</Text>
                          <Controller
                            name="salesRepresentativeIds"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                mode="multiple"
                                size="large"
                                placeholder={t('vendor.placeholders.salesRep')}
                                showSearch
                                allowClear
                                maxTagCount="responsive"
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={(value) => {
                                  // Convert undefined to empty array and enforce maximum 2 selections
                                  const newValue = value ?? [];
                                  if (newValue.length <= 2) {
                                    field.onChange(newValue);
                                  }
                                }}
                                options={salesRepresentatives.map((rep) => ({
                                  label: rep.name,
                                  value: rep.userId,
                                  disabled:
                                    (field.value?.length ?? 0) >= 2 &&
                                    !field.value?.includes(rep.userId),
                                }))}
                                tagRender={(props) => {
                                  const rep = salesRepresentatives.find(
                                    (r) => r.userId === props.value
                                  );
                                  return (
                                    <Tag
                                      closable={props.closable}
                                      onClose={props.onClose}
                                      style={{
                                        margin: '2px 4px 2px 0',
                                        padding: '0px 8px',
                                        fontSize: token.fontSizeSM,
                                        lineHeight: '24px',
                                        borderRadius: `${radiusTokens.control}px`,
                                        backgroundColor: token.colorPrimaryBg,
                                        color: token.colorPrimary,
                                        border: `1px solid ${token.colorPrimaryBorder}`,
                                      }}
                                    >
                                      {rep?.name}
                                    </Tag>
                                  );
                                }}
                                style={formStyles.inputStyle}
                                className="sales-rep-select"
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.primaryPort')}</Text>
                          <Controller
                            name="primaryPort"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                size="large"
                                placeholder={t('vendor.placeholders.primaryPort')}
                                showSearch
                                allowClear
                                onChange={(value) => field.onChange(value ?? '')}
                                options={[
                                  { label: t('vendor.ports.losAngeles'), value: 'USLA' },
                                  { label: t('vendor.ports.longBeach'), value: 'USLB' },
                                  { label: t('vendor.ports.shanghai'), value: 'CNSHA' },
                                  { label: t('vendor.ports.singapore'), value: 'SGSIN' },
                                  { label: t('vendor.ports.shenzhen'), value: 'CNSZX' },
                                  { label: t('vendor.ports.rotterdam'), value: 'NLRTM' },
                                ]}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {/* Row 2: Notes */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>{t('vendor.fields.notes')}</Text>
                          <Controller
                            name="notes"
                            control={control}
                            render={({ field }) => (
                              <Input.TextArea
                                {...field}
                                size="large"
                                placeholder={t('vendor.placeholders.notes')}
                                rows={3}
                                style={{
                                  borderRadius: `${radiusTokens.card}px`,
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Contacts Section — #91 view-only: contact create/edit lives in the Contacts module */}
                    <div style={fieldContainerStyle}>
                      <div
                        className="tw-flex tw-items-center tw-justify-between"
                        style={{ marginBottom: token.marginMD }}
                      >
                        <Text style={sectionTitleStyle}>{t('vendor.sections.contacts')}</Text>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          {isEditMode && (
                            <Checkbox
                              checked={includeInactiveContacts}
                              onChange={(e) => setIncludeInactiveContacts(e.target.checked)}
                            >
                              {t('vendor.labels.includeInactiveContacts')}
                            </Checkbox>
                          )}
                          {/* #91 方式2 — the add button LINKS to the Contacts module */}
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/auth/contacts')}
                            style={largeButtonStyle}
                          >
                            {t('vendor.contactsPolicy.addContact')}
                          </Button>
                        </div>
                      </div>
                      <Text type="secondary" style={{ display: 'block' }}>
                        {t('vendor.contactsPolicy.managedInModule')}
                      </Text>
                      {contacts.length > 0 && (
                        <TableContainer>
                          <Table
                            dataSource={[...contacts]
                              .filter((c) => includeInactiveContacts || c.isActive !== false)
                              .sort((a, b) => {
                                // Primary contact always comes first
                                if (a.isPrimary && !b.isPrimary) return -1;
                                if (!a.isPrimary && b.isPrimary) return 1;
                                return 0;
                              })}
                            rowKey="id"
                            rowClassName={(record) =>
                              record.isActive === false ? 'inactive-row' : ''
                            }
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            style={{ marginTop: token.marginMD }}
                            columns={[
                              {
                                title: t('vendor.table.primary'),
                                dataIndex: 'isPrimary',
                                key: 'isPrimary',
                                width: 120,
                                align: 'center',
                                render: (isPrimary: boolean) => (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: '100%',
                                      minHeight: '40px',
                                    }}
                                  >
                                    {isPrimary && (
                                      <CheckOutlined
                                        style={{
                                          color: token.colorPrimary,
                                          fontSize: token.sizeMD,
                                        }}
                                      />
                                    )}
                                  </div>
                                ),
                              },
                              {
                                title: t('vendor.table.name'),
                                dataIndex: 'name',
                                key: 'name',
                                width: 150,
                                ellipsis: true,
                                render: (text: string) => <Text strong>{text}</Text>,
                              },
                              {
                                title: t('vendor.table.jobTitle'),
                                dataIndex: 'jobTitle',
                                key: 'jobTitle',
                                width: 120,
                                ellipsis: true,
                              },
                              {
                                title: t('vendor.table.phone'),
                                key: 'phone',
                                width: 180,
                                render: (_: unknown, record: Contact) => {
                                  if (!record.phoneNumber) return '-';
                                  const parts = [record.phoneCountryCode, record.phoneNumber]
                                    .filter(Boolean)
                                    .join(' ');
                                  return record.phoneExtension
                                    ? `${parts} ext ${record.phoneExtension}`
                                    : parts;
                                },
                              },
                              {
                                title: t('vendor.table.mobile'),
                                key: 'mobile',
                                width: 150,
                                render: (_: unknown, record: Contact) => {
                                  if (!record.mobileNumber) return '-';
                                  const parts = [record.mobileCountryCode, record.mobileNumber]
                                    .filter(Boolean)
                                    .join(' ');
                                  return parts || '-';
                                },
                              },
                              {
                                title: t('vendor.table.fax'),
                                dataIndex: 'fax',
                                key: 'fax',
                                width: 150,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.table.email'),
                                dataIndex: 'email',
                                key: 'email',
                                width: 260,
                                ellipsis: true,
                              },
                              {
                                title: t('vendor.table.address'),
                                dataIndex: 'address',
                                key: 'address',
                                ellipsis: true,
                                render: (_: unknown, record: Contact) => (
                                  <ContactAddressDisplay contact={record} />
                                ),
                              },
                            ]}
                          />
                        </TableContainer>
                      )}
                    </div>

                    {/* Locations Section */}
                    <div style={fieldContainerStyle}>
                      <div
                        className="tw-flex tw-items-center tw-justify-between"
                        style={{ marginBottom: token.marginMD }}
                      >
                        <Text style={sectionTitleStyle}>{t('vendor.sections.locations')}</Text>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          {isEditMode && (
                            <Checkbox
                              checked={includeInactiveLocations}
                              onChange={(e) => setIncludeInactiveLocations(e.target.checked)}
                            >
                              {t('vendor.labels.includeInactiveLocations')}
                            </Checkbox>
                          )}
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              // Clear editing state for new location
                              setEditingLocation(null);
                              setEditingLocationFormData(null);
                              setEditingLocationId(null);
                              setLocationModalOpen(true);
                            }}
                            style={largeButtonStyle}
                          >
                            {t('vendor.buttons.addLocation')}
                          </Button>
                        </div>
                      </div>
                      {locations.length === 0 ? (
                        <Alert
                          message={t('vendor.empty.noLocations')}
                          description={t('vendor.empty.noLocationsDesc')}
                          type="info"
                          showIcon
                        />
                      ) : (
                        <TableContainer>
                          <Table
                            dataSource={[...locations]
                              .filter((l) => includeInactiveLocations || l.isActive !== false)
                              .sort((a, b) => {
                                // Default location always comes first
                                if (a.isDefaultReceiving && !b.isDefaultReceiving) return -1;
                                if (!a.isDefaultReceiving && b.isDefaultReceiving) return 1;
                                return 0;
                              })}
                            rowKey="id"
                            rowClassName={(record) =>
                              record.isActive === false ? 'inactive-row' : ''
                            }
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            style={{ marginTop: token.marginMD }}
                            columns={[
                              {
                                title: t('vendor.fields.defaultReceiving'),
                                dataIndex: 'isDefaultReceiving',
                                key: 'isDefaultReceiving',
                                width: 160,
                                align: 'center',
                                render: (isDefault: boolean, record: VendorLocation) => {
                                  const content = (
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                        minHeight: '40px',
                                      }}
                                    >
                                      {isDefault && (
                                        <CheckOutlined
                                          style={{
                                            color: token.colorPrimary,
                                            fontSize: token.sizeMD,
                                          }}
                                        />
                                      )}
                                    </div>
                                  );

                                  if (isEditMode && isNewItem(record.id)) {
                                    return (
                                      <Badge.Ribbon
                                        text={tCommon('new')}
                                        color={token.colorPrimary}
                                        placement="start"
                                      >
                                        <span>{content}</span>
                                      </Badge.Ribbon>
                                    );
                                  }
                                  return content;
                                },
                              },
                              {
                                title: t('vendor.fields.locationName'),
                                dataIndex: 'locationName',
                                key: 'locationName',
                                width: 180,
                                ellipsis: true,
                                render: (text: string) => <Text strong>{text}</Text>,
                              },
                              {
                                title: t('vendor.fields.address'),
                                key: 'address',
                                width: 300,
                                ellipsis: true,
                                render: (_: unknown, record: VendorLocation) =>
                                  // Existing locations from API have pre-formatted address
                                  // New locations need formatting from separate fields
                                  isNewItem(record.id)
                                    ? formatAddressForDisplay(record)
                                    : record.address || '-',
                              },
                              {
                                title: t('vendor.fields.notes'),
                                dataIndex: 'notes',
                                key: 'notes',
                                width: 200,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.actions'),
                                key: 'actions',
                                width: 80,
                                fixed: 'right',
                                render: (_: unknown, record: VendorLocation) => {
                                  const itemPerms = getItemPermissions(record);
                                  return (
                                    <Dropdown
                                      menu={{
                                        items: [
                                          {
                                            key: 'set-default',
                                            label: t('vendor.buttons.setAsDefault'),
                                            icon: <CheckCircleOutlined />,
                                            disabled: !itemPerms.canSetDefault,
                                            onClick: (e) => {
                                              e.domEvent.stopPropagation();
                                              handleSetAsDefaultLocation(record.id);
                                            },
                                          },
                                          {
                                            type: 'divider',
                                          },
                                          {
                                            key: 'edit',
                                            label: t('vendor.actions.edit'),
                                            icon: <EditOutlined />,
                                            disabled: !itemPerms.canEdit,
                                            onClick: (e) => {
                                              e.domEvent.stopPropagation();
                                              handleEditLocation(record);
                                            },
                                          },
                                          // In edit mode: existing items can be activated/deactivated, new items can be deleted
                                          // In create mode: all items can be deleted
                                          isEditMode && !isNewItem(record.id)
                                            ? {
                                                key: 'toggleStatus',
                                                label: itemPerms.isItemActive
                                                  ? t('vendor.actions.deactivate')
                                                  : t('vendor.actions.activate'),
                                                icon: itemPerms.isItemActive ? (
                                                  <CloseCircleOutlined />
                                                ) : (
                                                  <CheckCircleOutlined />
                                                ),
                                                ...getToggleStatusStyle(itemPerms, token),
                                                onClick: (e) => {
                                                  e.domEvent.stopPropagation();
                                                  handleToggleLocationStatus(record.id);
                                                },
                                              }
                                            : {
                                                key: 'delete',
                                                label: t('vendor.actions.delete'),
                                                icon: <DeleteOutlined />,
                                                danger: true,
                                                disabled: record.isDefaultReceiving,
                                                onClick: (e) => {
                                                  e.domEvent.stopPropagation();
                                                  handleDeleteLocation(record.id);
                                                },
                                              },
                                        ],
                                      }}
                                      trigger={['click']}
                                    >
                                      <Button
                                        type="text"
                                        icon={<MoreOutlined />}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </Dropdown>
                                  );
                                },
                              },
                            ]}
                          />
                        </TableContainer>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Financial Information */}
                {currentStep === 1 && (
                  <div>
                    {/* Payment Terms Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('vendor.sections.paymentTerms')}
                      </Text>
                      {/* Row 1: Deposit + Cash Discount (always visible) */}
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.labels.deposit')}</Text>
                          <Controller
                            name="deposit"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                type="number"
                                min={0}
                                max={100}
                                placeholder={t('vendor.placeholders.depositPlaceholder')}
                                suffix="%"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : undefined;
                                  field.onChange(value);
                                }}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.labels.cashDiscount')}</Text>
                          <Controller
                            name="cashDiscount"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                type="number"
                                min={0}
                                max={100}
                                placeholder={t('vendor.placeholders.cashDiscountPlaceholder')}
                                suffix="%"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : undefined;
                                  field.onChange(value);
                                }}
                                style={formStyles.inputStyle}
                              />
                            )}
                          />
                        </Col>
                      </Row>

                      {/* Row 2: Agreed Terms + Due In Days */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.agreedTerms')}
                          </Text>
                          <Controller
                            name="termsTypeId"
                            control={control}
                            rules={{ required: t('vendor.validation.categoryRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  size="large"
                                  placeholder={t('vendor.placeholders.agreedTerms')}
                                  loading={isLoadingPaymentTerms}
                                  options={paymentTerms.map((term) => {
                                    const termName =
                                      i18n.language === 'zh' ? term.termNameZh : term.termNameEn;
                                    return {
                                      label: `${termName} (${term.termCode})`,
                                      value: term.paymentTermId,
                                      description: term.description,
                                    };
                                  })}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.termsTypeId ? token.colorError : undefined,
                                  }}
                                />
                                {errors.termsTypeId && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.termsTypeId.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.days')}</Text>
                          <Controller
                            name="termsDays"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                type="number"
                                placeholder={
                                  isZeroDayTerm
                                    ? t('vendor.paymentTerms.notApplicable')
                                    : t('vendor.placeholders.enterDays')
                                }
                                suffix={t('vendor.misc.suffix.days')}
                                disabled={isZeroDayTerm}
                                value={isZeroDayTerm ? '' : (field.value ?? '')}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : undefined;
                                  field.onChange(value);
                                }}
                                style={{
                                  ...formStyles.inputStyle,
                                  backgroundColor: isZeroDayTerm
                                    ? token.colorBgContainerDisabled
                                    : token.colorBgContainer,
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>

                      {/* Row 3: Cash Discount Rate + Discount Days (only when payment term has discount) */}
                      {termHasDiscount && (
                        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                          <Col span={12}>
                            <Text style={labelStyle}>{t('vendor.fields.cashDiscountRate')}</Text>
                            <Controller
                              name="cashDiscountRate"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  size="large"
                                  type="number"
                                  min={0}
                                  max={100}
                                  placeholder={selectedPaymentTerm?.discountRate?.toString() || '0'}
                                  suffix="%"
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    const value = e.target.value
                                      ? Number(e.target.value)
                                      : undefined;
                                    field.onChange(value);
                                  }}
                                  style={formStyles.inputStyle}
                                />
                              )}
                            />
                          </Col>
                          <Col span={12}>
                            <Text style={labelStyle}>{t('vendor.fields.cashDiscountDays')}</Text>
                            <Controller
                              name="cashDiscountDays"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  size="large"
                                  type="number"
                                  min={0}
                                  placeholder={selectedPaymentTerm?.discountDays?.toString() || '0'}
                                  suffix={t('vendor.misc.suffix.days')}
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    const value = e.target.value
                                      ? Number(e.target.value)
                                      : undefined;
                                    field.onChange(value);
                                  }}
                                  style={formStyles.inputStyle}
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      )}

                      {/* Row 4: Primary Currency */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={24}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.primaryCurrency')}
                          </Text>
                          <Controller
                            name="primaryCurrency"
                            control={control}
                            rules={{ required: t('vendor.validation.categoryRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  size="large"
                                  placeholder={t('vendor.placeholders.currency')}
                                  showSearch
                                  loading={isLoadingCurrencies}
                                  options={currencyOptions}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.primaryCurrency
                                      ? token.colorError
                                      : undefined,
                                  }}
                                />
                                {errors.primaryCurrency && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.primaryCurrency.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* Tax Section */}
                    <div style={fieldContainerStyle}>
                      <Text strong style={sectionTitleStyle}>
                        {t('vendor.sections.taxInfo')}
                      </Text>
                      {/* Row 1: ABN (Display only - from quick-create) */}
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Text style={labelStyle}>{t('vendor.fields.taxIdAbn')}</Text>
                          <Controller
                            name="abn"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                readOnly
                                style={{
                                  ...formStyles.inputStyle,
                                  backgroundColor: token.colorBgContainer,
                                  color: token.colorText,
                                  cursor: 'not-allowed',
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>

                      {/* Row 2: Tax Type + Tax Rate */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                          <Text style={labelStyle}>
                            <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                            {t('vendor.fields.taxType')}
                          </Text>
                          <Controller
                            name="taxTypeId"
                            control={control}
                            rules={{ required: t('vendor.validation.categoryRequired') }}
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  size="large"
                                  placeholder={t('vendor.placeholders.taxType')}
                                  loading={isLoadingTaxTypes}
                                  options={taxTypes.map((tax) => ({
                                    label: `${tax.taxType} (${tax.taxRate}%)`,
                                    value: tax.taxTypeId,
                                  }))}
                                  style={{
                                    ...formStyles.inputStyle,
                                    borderColor: errors.taxTypeId ? token.colorError : undefined,
                                  }}
                                />
                                {errors.taxTypeId && (
                                  <Text
                                    style={{ fontSize: token.fontSizeSM, color: token.colorError }}
                                  >
                                    {errors.taxTypeId.message}
                                  </Text>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={12}>
                          <Text style={labelStyle}>{t('vendor.fields.taxRate')}</Text>
                          <Controller
                            name="taxRate"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                size="large"
                                type="number"
                                placeholder={t('vendor.fields.autoCalculated')}
                                suffix="%"
                                readOnly
                                style={{
                                  ...formStyles.inputStyle,
                                  backgroundColor: token.colorBgContainer,
                                  color: token.colorText,
                                  cursor: 'not-allowed',
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}

                {/* Step 3: Bank Accounts */}
                {currentStep === 2 && (
                  <div>
                    {/* Bank Accounts Section */}
                    <div style={fieldContainerStyle}>
                      {/* Header with title and controls */}
                      <div
                        className="tw-flex tw-items-center tw-justify-between"
                        style={{ marginBottom: token.marginMD }}
                      >
                        <Text strong style={sectionTitleStyle}>
                          {t('vendor.tabs.bankAccounts')}
                        </Text>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          {isEditMode && (
                            <Checkbox
                              checked={includeInactiveBankAccounts}
                              onChange={(e) => setIncludeInactiveBankAccounts(e.target.checked)}
                            >
                              {t('vendor.labels.includeInactiveBankAccounts')}
                            </Checkbox>
                          )}
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddBankAccount}
                            size="large"
                            style={{ height: `${controlHeightTokens.controlHeight}px` }}
                          >
                            {t('vendor.buttons.add')}
                          </Button>
                        </div>
                      </div>

                      {/* Bank Accounts Table */}
                      {bankAccounts.length === 0 ? (
                        <Alert
                          message={t('vendor.empty.noBankAccounts')}
                          description={t('vendor.empty.noBankAccountsDesc')}
                          type="info"
                          showIcon
                        />
                      ) : (
                        <TableContainer>
                          <Table
                            dataSource={[...bankAccounts]
                              .filter((b) => includeInactiveBankAccounts || b.isActive !== false)
                              .map((acc, index) => ({
                                ...acc,
                                key: index,
                                originalIndex: bankAccounts.findIndex((b) => b === acc),
                              }))}
                            rowClassName={(record) =>
                              record.isActive === false ? 'inactive-row' : ''
                            }
                            onRow={(record) => ({
                              onClick: () => handleViewBankAccount(record.originalIndex),
                              style: { cursor: 'pointer' },
                            })}
                            columns={[
                              {
                                title: t('vendor.fields.default'),
                                dataIndex: 'isDefault',
                                key: 'isDefault',
                                width: 100,
                                align: 'center',
                                render: (isDefault: boolean, record: BankAccountTableRecord) => {
                                  const content = (
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                        minHeight: '40px',
                                      }}
                                    >
                                      {isDefault && (
                                        <CheckOutlined
                                          style={{
                                            color: token.colorPrimary,
                                            fontSize: token.sizeMD,
                                          }}
                                        />
                                      )}
                                    </div>
                                  );

                                  if (isEditMode && !record.vendorBankId) {
                                    return (
                                      <Badge.Ribbon
                                        text={tCommon('new')}
                                        color={token.colorPrimary}
                                        placement="start"
                                      >
                                        <span>{content}</span>
                                      </Badge.Ribbon>
                                    );
                                  }
                                  return content;
                                },
                              },
                              {
                                title: t('vendor.fields.accountName'),
                                dataIndex: 'accountName',
                                key: 'accountName',
                                width: 150,
                                ellipsis: true,
                                render: (text: string) => <Text strong>{text}</Text>,
                              },
                              {
                                title: t('vendor.fields.bankCode'),
                                dataIndex: 'bankCode',
                                key: 'bankCode',
                                width: 100,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.bankName'),
                                dataIndex: 'bankName',
                                key: 'bankName',
                                width: 150,
                                ellipsis: true,
                              },
                              {
                                title: t('vendor.fields.branchName'),
                                dataIndex: 'branchName',
                                key: 'branchName',
                                width: 130,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.currency'),
                                dataIndex: 'currency',
                                key: 'currency',
                                width: 100,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.accountNumber'),
                                dataIndex: 'accountNumber',
                                key: 'accountNumber',
                                width: 150,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.swiftCode'),
                                dataIndex: 'swiftCode',
                                key: 'swiftCode',
                                width: 120,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.iban'),
                                dataIndex: 'iban',
                                key: 'iban',
                                width: 150,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.bankLocation'),
                                dataIndex: 'bankLocation',
                                key: 'bankLocation',
                                width: 120,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.notes'),
                                dataIndex: 'notes',
                                key: 'notes',
                                width: 150,
                                ellipsis: true,
                                render: (text: string) => text || '-',
                              },
                              {
                                title: t('vendor.fields.action'),
                                key: 'actions',
                                width: 80,
                                fixed: 'right',
                                render: (_: unknown, record: BankAccountTableRecord) => {
                                  const itemPerms = getItemPermissions(record);
                                  return (
                                    <Dropdown
                                      menu={{
                                        items: [
                                          {
                                            key: 'set-default',
                                            label: t('vendor.buttons.setAsDefault'),
                                            icon: <CheckCircleOutlined />,
                                            disabled: !itemPerms.canSetDefault,
                                            onClick: (e) => {
                                              e.domEvent.stopPropagation();
                                              handleSetAsDefaultBankAccount(record.originalIndex);
                                            },
                                          },
                                          {
                                            type: 'divider',
                                          },
                                          {
                                            key: 'edit',
                                            label: t('vendor.actions.edit'),
                                            icon: <EditOutlined />,
                                            disabled: !itemPerms.canEdit,
                                            onClick: (e) => {
                                              e.domEvent.stopPropagation();
                                              handleEditBankAccount(record.originalIndex);
                                            },
                                          },
                                          // In edit mode: existing items can be activated/deactivated, new items can be deleted
                                          // In create mode: all items can be deleted
                                          isEditMode && record.vendorBankId
                                            ? {
                                                key: 'toggleStatus',
                                                label: itemPerms.isItemActive
                                                  ? t('vendor.actions.deactivate')
                                                  : t('vendor.actions.activate'),
                                                icon: itemPerms.isItemActive ? (
                                                  <CloseCircleOutlined />
                                                ) : (
                                                  <CheckCircleOutlined />
                                                ),
                                                ...getToggleStatusStyle(itemPerms, token),
                                                onClick: (e) => {
                                                  e.domEvent.stopPropagation();
                                                  handleToggleBankAccountStatus(
                                                    record.originalIndex
                                                  );
                                                },
                                              }
                                            : {
                                                key: 'delete',
                                                label: t('vendor.actions.delete'),
                                                icon: <DeleteOutlined />,
                                                danger: true,
                                                disabled: record.isDefault,
                                                onClick: (e) => {
                                                  e.domEvent.stopPropagation();
                                                  handleDeleteBankAccount(record.originalIndex);
                                                },
                                              },
                                        ],
                                      }}
                                      trigger={['click']}
                                    >
                                      <Button
                                        type="text"
                                        icon={<MoreOutlined />}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </Dropdown>
                                  );
                                },
                              },
                            ]}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            locale={{
                              emptyText: t('vendor.empty.noBankAccountsYet'),
                            }}
                          />
                        </TableContainer>
                      )}
                    </div>
                  </div>
                )}
              </Form>
            </div>
          </div>

          {/* Sticky Bottom Buttons */}
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 300, // Sider width
              right: 0,
              height: `${FOOTER_HEIGHT}px`,
              backgroundColor: token.colorBgContainer,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              padding: `${token.paddingMD}px ${token.paddingLG}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 99,
            }}
          >
            {/* Left: Exit button (always visible) */}
            <Button danger size="large" onClick={handleBack} style={largeButtonStyle}>
              {t('vendor.buttons.exit')}
            </Button>

            {/* Right: Save/Previous/Next/Submit buttons */}
            <div className="tw-flex tw-gap-3">
              {/* Previous button (hidden on step 1) */}
              {currentStep > 0 && (
                <Button
                  size="large"
                  onClick={handlePrevious}
                  style={largeButtonStyle}
                  disabled={isSaving}
                >
                  {t('vendor.actions.previous')}
                </Button>
              )}

              {/* Next button (hidden on last step) */}
              {currentStep < stepItems.length - 1 && (
                <Button
                  size="large"
                  onClick={handleNext}
                  style={largeButtonStyle}
                  disabled={isSaving}
                >
                  {t('vendor.actions.next')}
                </Button>
              )}

              {/* Save button - Edit mode: shown on ALL steps with cumulative save
                  Create mode: only on last step as "Create Vendor" */}
              {isEditMode ? (
                <Button
                  type="primary"
                  size="large"
                  onClick={saveCurrentStep}
                  loading={isSaving}
                  style={largeButtonStyle}
                >
                  {t('vendor.actions.save')}
                </Button>
              ) : (
                currentStep === stepItems.length - 1 && (
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleSubmit(onSubmit)}
                    style={largeButtonStyle}
                    loading={isSaving}
                    disabled={isSaving}
                  >
                    {t('vendor.buttons.createVendor')}
                  </Button>
                )
              )}
            </div>
          </div>
        </Content>
      </Layout>

      {/* Location Modal */}
      <LocationFormModal
        visible={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setEditingLocation(null);
          setEditingLocationFormData(null);
          setEditingLocationId(null);
        }}
        onSuccess={handleAddLocation}
        location={
          editingLocationId
            ? locationDetailLoading
              ? null
              : locationDetailFormData
            : editingLocationFormData
        }
      />

      <BankAccountFormModal
        visible={bankAccountModalOpen}
        onClose={handleCloseBankAccountModal}
        onSuccess={handleSaveBankAccount}
        bankAccount={
          editingBankAccountIndex !== null ? bankAccounts[editingBankAccountIndex] : null
        }
        readOnly={bankAccountReadOnly}
      />

      {/* Company Found Confirmation Modal */}
      <Modal
        open={validationMachine.showModal}
        onCancel={handleCancelCompanyModal}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <WarningOutlined style={{ color: token.colorWarning, fontSize: '20px' }} />
            <span>{t('vendor.modals.companyFoundTitle')}</span>
          </div>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <Button
              onClick={handleCancelCompanyModal}
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            >
              {t('vendor.actions.cancel')}
            </Button>
            <Button
              type="primary"
              loading={isLoadingCompanyData}
              onClick={handleLoadCompanyData}
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            >
              {t('vendor.actions.loadData')}
            </Button>
          </div>
        }
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          <Typography.Paragraph>{t('vendor.modals.companyFoundDescription')}</Typography.Paragraph>
          {validationMachine.localTaxId && (
            <div
              style={{
                backgroundColor: token.colorFillQuaternary,
                borderRadius: radiusTokens.panel,
                padding: '16px',
                marginTop: '12px',
              }}
            >
              <Typography.Text strong style={{ display: 'block', marginBottom: '8px' }}>
                {t('vendor.fields.taxIdAbn')}:{' '}
                <span style={{ fontFamily: 'monospace' }}>{validationMachine.localTaxId}</span>
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('vendor.modals.companyExistsNotVendor')}
              </Typography.Text>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default VendorFormWizardContent;
